import { trackGa4Checkout } from '../../lib/ga4';
import React, {useCallback, useEffect, useMemo, useState, useRef, useContext} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import md5 from 'js-md5';
import { useMarketplaceExperiment, useUserContext } from "../../contexts";
import { meFn, userFn, updateUserProfileFn, teamFn, expireAllSessions, getAssetsAvailability, getProducts, getPlanAddonsFn, addSubscriptionAddonFn, subscribeUserFn, pauseSubscriptionFn, resumeSubscriptionFn, cancelSubscription, confirmSubscriptionCancellation, createStripePaymentMethodUpdateLink, deleteUserFn, getInvoices, getBundlesFn, getPurchasedBundlesFn, getMarketplaceOrdersFn, getMarketplacePurchasedAssetsFn, Analytics} from '../../lib/api';
import '../../styles/layout.scss';
import './Account.scss';
import AccountLocationBanners from './AccountLocationBanners';
import { Membership } from '../Member';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { Spinner } from '../Spinner';
import { FormatSelectionWithVideo } from '../FormatSelection';
import { Favorites } from '../Favorites';
import { Consumptions } from '../Consumptions';
import { Input, SlideToggle } from '../Form';
import { getCookie, getReadableDate, getEnvironment } from '../../lib/utils';
import { getProductioncrateSiteHref, isStripePaymentPastDue } from '../Header/accountIdentity';
import { hasPremiumAssetsAccess, hasPremiumPluginsAccess } from '../../lib/subscriptionCapabilities';
import { tab } from '@testing-library/user-event/dist/tab';
import { Modal } from '../Modal';
import { SubscriptionUpgradeModals } from '../Plans';
import { Questionnaire } from '../Questionnaire';
import configValues from '../../lib/config';
import { TeamLicenseManager } from '../TeamLicenseManager';

const TEAM_SEAT_CONTEXTS = [
    {key: 'desktop', label: 'Desktop'},
    {key: 'terminal', label: 'Terminal'}
];

const TEAM_SEAT_FEATURE_BY_CONTEXT = {
    desktop: 'desktop-seats-on-team',
    terminal: 'terminal-seats-on-team'
};

const TEAM_SEAT_FEATURE_FLAGS = Object.values(TEAM_SEAT_FEATURE_BY_CONTEXT);

const parseSeatMetric = (value) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getSeatDeviceLabel = (seat, fallbackContext = 'desktop') => {
    const candidateDeviceLabel = seat?.device_label || seat?.device_name || seat?.device;
    if(candidateDeviceLabel && String(candidateDeviceLabel).trim()){
        return String(candidateDeviceLabel).trim();
    }

    const resolvedContext = String(seat?.seat_context || fallbackContext || '').toLowerCase();
    return resolvedContext === 'terminal' ? 'Terminal session' : 'Desktop device';
};

const normalizeSeatContextPayload = (contextKey, seatPayload = {}) => {
    const payload = seatPayload && typeof seatPayload === 'object' ? seatPayload : {};
    const seatsTotal = parseSeatMetric(payload.seats_total);
    const seatsUsed = parseSeatMetric(payload.seats_used);
    const seatsAvailable = payload.seats_available === null || payload.seats_available === undefined
        ? Math.max(0, seatsTotal - seatsUsed)
        : parseSeatMetric(payload.seats_available);

    return {
        seat_context: payload.seat_context || contextKey,
        seats_total: seatsTotal,
        seats_used: seatsUsed,
        seats_available: seatsAvailable,
        seats: Array.isArray(payload.seats)
            ? payload.seats.map((seat) => ({
                ...seat,
                seat_context: seat?.seat_context || contextKey,
                device_label: getSeatDeviceLabel(seat, contextKey)
            }))
            : [],
        by_user: Array.isArray(payload.by_user) ? payload.by_user : []
    };
};

const normalizeTeamSeatsPayload = (rawPayload) => {
    if(!rawPayload || typeof rawPayload !== 'object'){
        return null;
    }

    if(typeof rawPayload.seat_context === 'string'){
        const contextKey = rawPayload.seat_context.toLowerCase();
        return TEAM_SEAT_CONTEXTS.reduce((accumulator, context) => {
            accumulator[context.key] = normalizeSeatContextPayload(
                context.key,
                context.key === contextKey ? rawPayload : {}
            );
            return accumulator;
        }, {});
    }

    return TEAM_SEAT_CONTEXTS.reduce((accumulator, context) => {
        accumulator[context.key] = normalizeSeatContextPayload(context.key, rawPayload?.[context.key]);
        return accumulator;
    }, {});
};

const getTeamSeatsErrorMessage = (statusCode, defaultMessage) => {
    if(statusCode === 401){
        return 'Your session is invalid or expired. Please sign in again.';
    }
    if(statusCode === 403){
        return 'Only team owners can view seat usage.';
    }
    if(statusCode === 404){
        return 'Team not found.';
    }
    return defaultMessage || 'Unable to load team seats usage.';
};

const hasPlanFeature = (features, featureName) => {
    if(!Array.isArray(features)){
        return false;
    }
    return features.some((feature) => feature?.name === featureName);
};

const hasTeamSeatFeatureEnabled = (features) => {
    return TEAM_SEAT_FEATURE_FLAGS.some((featureName) => hasPlanFeature(features, featureName));
};

const hasTeamSeatContextFeatureEnabled = (features, contextKey) => {
    const featureName = TEAM_SEAT_FEATURE_BY_CONTEXT[contextKey];
    return featureName ? hasPlanFeature(features, featureName) : false;
};

export const getBundleMemberCapacity = (teamBundles) => {
    const entries = Array.isArray(teamBundles)
        ? teamBundles
        : (Array.isArray(teamBundles?.data) ? teamBundles.data : []);

    return entries.reduce((capacity, teamBundle) => {
        const licensedMembers = Number(teamBundle?.licensed_members);
        return Number.isFinite(licensedMembers) && licensedMembers > capacity ? licensedMembers : capacity;
    }, 0);
};

export const getTeamMemberLimit = (user, bundleMemberCapacity = 0) => {
    const userCapacity = Number(user?.team?.member_capacity);
    const enterpriseCapacity = Number(user?.isEnterprise);
    const bundleCapacity = Number(bundleMemberCapacity);
    const capacities = [userCapacity, enterpriseCapacity, bundleCapacity]
        .filter((capacity) => Number.isFinite(capacity) && capacity > 0);

    return capacities.length > 0 ? Math.max(...capacities) : 0;
};

export const canShowTeamTab = (user) => !!user?.is_team_owner;

export const canInviteTeamMembers = (user, bundleMemberCapacity = 0) => !!user?.is_team_owner && getTeamMemberLimit(user, bundleMemberCapacity) > 1;

export const getUserIdentityLabel = (user) => {
    const email = String(user?.email || '').trim();
    const username = String(user?.username || '').trim();
    if(!username || username.toLowerCase() === email.toLowerCase()){
        return email;
    }
    return email ? `${username} / ${email}` : username;
};

export const getTeamDisplayName = (user) => {
    const owner = user?.team?.owner || user?.team?.owner_user || user?.team?.ownerUser;
    const fullName = [owner?.first_name, owner?.last_name]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' ');
    if(fullName && !/^no[_\s-]?firstname\s+no[_\s-]?lastname$/i.test(fullName)){
        return fullName;
    }

    const ownerUsername = String(owner?.username || '').trim();
    if(ownerUsername && !/^no[_\s-]?firstname\s+no[_\s-]?lastname$/i.test(ownerUsername)){
        return ownerUsername;
    }

    const ownerEmail = String(owner?.email || '').trim();
    if(ownerEmail){
        return ownerEmail;
    }

    return String(user?.username || user?.email || 'your').trim();
};

const getTeamLicenseCollection = (payload) => (
    Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : [])
);

const getLicenseMemberId = (member) => member?.id ?? member?.user_id ?? member?.userId ?? member?.user?.id ?? null;

export const getAccessiblePluginLicenses = (teamLicenses, user) => {
    const currentUserId = user?.id ?? null;
    const currentUserEmail = String(user?.email || '').trim().toLowerCase();
    return getTeamLicenseCollection(teamLicenses).flatMap((teamBundle) => {
        const bundleName = teamBundle?.bundle?.name || teamBundle?.bundle_name || 'Team bundle';
        return (Array.isArray(teamBundle?.apps) ? teamBundle.apps : [])
            .filter((appLicense) => {
                const assignments = Array.isArray(appLicense?.assignments) ? appLicense.assignments : [];
                return assignments.some((assignment) => {
                    const assignmentId = getLicenseMemberId(assignment);
                    const assignmentEmail = String(assignment?.email || assignment?.user?.email || '').trim().toLowerCase();
                    return (currentUserId !== null && assignmentId !== null && String(assignmentId) === String(currentUserId)) ||
                        (currentUserEmail && assignmentEmail === currentUserEmail);
                });
            })
            .map((appLicense) => ({
                id: appLicense?.app?.id ?? appLicense?.app_id ?? appLicense?.id,
                name: appLicense?.app?.name || appLicense?.app?.slug || appLicense?.name || 'Plugin license',
                bundleName,
            }));
    });
};

const getTeamMemberId = (member) => {
    return member?.id ?? member?.user_id ?? member?.userId ?? member?.user?.id ?? null;
};

const isSameUserId = (leftId, rightId) => {
    if(leftId === null || leftId === undefined || rightId === null || rightId === undefined){
        return false;
    }
    return String(leftId) === String(rightId);
};

const isTeamMemberOwner = (member, user) => {
    const role = String(member?.role ?? member?.team_role ?? member?.teamRole ?? '').toLowerCase();
    return Boolean(
        member?.is_team_owner ||
        member?.is_owner ||
        member?.isOwner ||
        role === 'owner' ||
        (user?.is_team_owner && isSameUserId(getTeamMemberId(member), user?.id))
    );
};

const canRemoveTeamMember = (member, user) => {
    return Boolean(user?.is_team_owner && !isTeamMemberOwner(member, user));
};

const ADDON_PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const MARKETPLACE_DATE_FORMAT_OPTIONS = {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
};

const formatMarketplacePrice = (value, currency = 'USD') => {
    const parsedValue = Number(value);
    if(!Number.isFinite(parsedValue)){
        return '$0.00';
    }

    const normalizedCurrency = String(currency || 'USD').trim().toUpperCase();
    try{
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: normalizedCurrency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parsedValue);
    }catch(error){
        return ADDON_PRICE_FORMATTER.format(parsedValue);
    }
};

const formatMarketplaceDateTime = (value, fallback = 'Pending') => {
    if(!value){
        return fallback;
    }
    const parsedDate = new Date(value);
    if(Number.isNaN(parsedDate.getTime())){
        return fallback;
    }
    return parsedDate.toLocaleString('en-US', MARKETPLACE_DATE_FORMAT_OPTIONS);
};

const getMarketplaceOrderAssetItems = (order) => {
    if(!Array.isArray(order?.items)){
        return [];
    }
    return order.items.filter((item) => item?.line_type === 'asset');
};

const hasMarketplaceAssetOrderItems = (order) => getMarketplaceOrderAssetItems(order).length > 0;

const getMarketplaceOrderStatusLabel = (value) => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if(!normalizedValue){
        return 'Unknown';
    }
    return normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
};

const formatAddonPrice = (value) => {
    const parsedValue = Number(value);
    if(!Number.isFinite(parsedValue)){
        return '$0.00';
    }
    return ADDON_PRICE_FORMATTER.format(parsedValue);
};
const getAddonPriceId = (addon) => {
    const parsedAddonPriceId = Number(addon?.price?.id ?? addon?.addon_price_id ?? addon?.price_id);
    if(!Number.isFinite(parsedAddonPriceId)){
        return null;
    }
    return parsedAddonPriceId;
};
const getAddonPriceValue = (addon) => {
    const parsedAddonPriceValue = Number(addon?.price?.price ?? addon?.price ?? 0);
    if(!Number.isFinite(parsedAddonPriceValue)){
        return 0;
    }
    return parsedAddonPriceValue;
};
const getAddonPeriodicityType = (addon) => {
    return String(addon?.price?.periodicity_type ?? addon?.periodicity_type ?? '').trim();
};

const normalizeSubscriptionState = (value) => String(value ?? '').trim().toLowerCase();
export const ACCOUNT_SECTION_PATHS = {
    settings: '/account',
    team: '/account/teams-seats',
    licenses: '/account/licenses',
    consumptions: '/account/assets',
    subscription: '/account/billing'
};

export const resolveAccountTabFromPath = (pathname = '') => {
    const normalizedPathname = String(pathname || '').replace(/\/+$/, '') || '/';

    if(normalizedPathname === '/member' || normalizedPathname === ACCOUNT_SECTION_PATHS.settings){
        return 'settings';
    }

    const matchingEntry = Object.entries(ACCOUNT_SECTION_PATHS)
        .find(([, path]) => path !== ACCOUNT_SECTION_PATHS.settings && normalizedPathname === path);

    return matchingEntry?.[0] || 'settings';
};

const isSubscriptionCancelled = (subscription) => {
    const normalizedState = normalizeSubscriptionState(subscription?.state);
    return normalizedState === 'cancelled' || normalizedState === 'canceled';
};

const isSubscriptionPaused = (subscription) => {
    const normalizedState = normalizeSubscriptionState(subscription?.state);
    return subscription?.paused === true || normalizedState === 'paused';
};

const normalizeBundleCheckoutStatus = (value) => {
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    if(normalizedValue === 'success'){
        return 'success';
    }
    if(['cancel', 'cancelled', 'canceled'].includes(normalizedValue)){
        return 'cancelled';
    }
    return null;
};

export const isChurnkeyCancellationResult = (sessionResults) => {
    const status = String(sessionResults?.status ?? '').trim().toLowerCase();
    const result = String(sessionResults?.result ?? '').trim().toLowerCase();
    return ['cancel', 'canceled', 'cancelled'].includes(status)
        || ['cancel', 'canceled', 'cancelled'].includes(result);
};

export const isCancellationConfirmationStatus = (value) => (
    ['cancelled', 'canceled'].includes(String(value ?? '').trim().toLowerCase())
);

const waitForCancellationCheck = (milliseconds) => new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
});

const withCancellationCheckTimeout = (promise, milliseconds) => new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
        reject(new Error('Cancellation confirmation timed out.'));
    }, milliseconds);

    promise.then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
    }).catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
    });
});

const normalizeBundleToken = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
const PROFILE_FIELD_LABELS = {
    first_name: 'First name',
    last_name: 'Last name',
    username: 'Username'
};

const PROFILE_INPUT_PROPS = {
    first_name: {
        name: 'pc_profile_primary',
        autoComplete: 'off',
        'data-lpignore': 'true',
        'data-1p-ignore': 'true',
    },
    last_name: {
        name: 'pc_profile_secondary',
        autoComplete: 'off',
        'data-lpignore': 'true',
        'data-1p-ignore': 'true',
    },
    username: {
        name: 'pc_profile_handle',
        autoComplete: 'new-password',
        'data-lpignore': 'true',
        'data-1p-ignore': 'true',
        'data-bwignore': 'true',
        'data-form-type': 'other',
    }
};

const getPersonalInformationValue = (user, field) => {
    if(['first_name', 'last_name', 'username'].includes(field)){
        return user?.[field] ?? '';
    }

    const value = user?.settings?.[field];
    if(['profession', 'software_preferences', 'usage_purpose'].includes(field)){
        if(Array.isArray(value)){
            return value[0] ?? '';
        }
        return value?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') ?? '';
    }

    return value ?? '';
};

const getProfileFieldErrorMessage = (error, field) => {
    const fieldErrors = error?.errors?.[field];
    if(Array.isArray(fieldErrors) && typeof fieldErrors[0] === 'string' && fieldErrors[0].trim()){
        return fieldErrors[0];
    }
    if(typeof error?.message === 'string' && error.message.trim()){
        return error.message;
    }
    return `${PROFILE_FIELD_LABELS[field] || 'Profile field'} could not be updated.`;
};

const tryParseJsonString = (value) => {
    if(typeof value !== 'string'){
        return null;
    }
    try {
        return JSON.parse(value);
    } catch (error){
        return null;
    }
};

const toMetadataValueString = (value) => {
    if(value === null || value === undefined){
        return null;
    }
    if(typeof value === 'string'){
        const trimmedValue = value.trim();
        return trimmedValue || null;
    }
    if(typeof value === 'number' || typeof value === 'boolean'){
        return String(value);
    }
    if(Array.isArray(value)){
        const joinedValue = value
            .map((entry) => toMetadataValueString(entry))
            .filter((entry) => !!entry)
            .join(', ');
        return joinedValue || null;
    }
    if(typeof value === 'object'){
        if(Object.prototype.hasOwnProperty.call(value, 'url')){
            return toMetadataValueString(value.url);
        }
        if(Object.prototype.hasOwnProperty.call(value, 'src')){
            return toMetadataValueString(value.src);
        }
        if(Object.prototype.hasOwnProperty.call(value, 'value')){
            return toMetadataValueString(value.value);
        }
        if(Object.prototype.hasOwnProperty.call(value, 'content')){
            return toMetadataValueString(value.content);
        }
        if(Object.prototype.hasOwnProperty.call(value, 'data')){
            return toMetadataValueString(value.data);
        }
    }
    return null;
};

const getMetadataEntries = (source) => {
    if(!source){
        return [];
    }
    if(Array.isArray(source)){
        return source
            .map((entry) => {
                if(!entry || typeof entry !== 'object'){
                    return null;
                }
                const metadataKey = entry.key ?? entry.name ?? entry.slug ?? entry.id;
                if(metadataKey === null || metadataKey === undefined){
                    return null;
                }
                const metadataValue = entry.value ?? entry.content ?? entry.data ?? entry.meta_value ?? entry;
                return [String(metadataKey), metadataValue];
            })
            .filter((entry) => Array.isArray(entry));
    }
    if(typeof source === 'object'){
        return Object.entries(source);
    }
    return [];
};

const getBundleMetadataValue = (bundle, key) => {
    const normalizedKey = normalizeBundleToken(key);
    const rawMetadata = bundle?.metadata;
    const parsedMetadata = typeof rawMetadata === 'string'
        ? tryParseJsonString(rawMetadata)
        : rawMetadata;

    const metadataSources = [parsedMetadata].filter((source) => !!source);
    if(parsedMetadata && typeof parsedMetadata === 'object' && !Array.isArray(parsedMetadata)){
        ['metadata', 'data', 'attributes', 'meta', 'fields', 'items'].forEach((nestedSourceKey) => {
            const nestedSourceValue = parsedMetadata[nestedSourceKey];
            const parsedNestedSource = typeof nestedSourceValue === 'string'
                ? tryParseJsonString(nestedSourceValue)
                : nestedSourceValue;
            if(parsedNestedSource){
                metadataSources.push(parsedNestedSource);
            }
        });
    }

    for(const metadataSource of metadataSources){
        const metadataEntries = getMetadataEntries(metadataSource);
        for(const [metadataKey, metadataValue] of metadataEntries){
            if(normalizeBundleToken(metadataKey) !== normalizedKey){
                continue;
            }
            const resolvedValue = toMetadataValueString(metadataValue);
            if(resolvedValue){
                return resolvedValue;
            }
        }
    }

    return null;
};

const toSafeHttpUrl = (value) => {
    const stringValue = toMetadataValueString(value);
    if(!stringValue){
        return null;
    }

    try {
        const parsedUrl = new URL(stringValue, window.location.origin);
        if(parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'){
            return parsedUrl.toString();
        }
    } catch (error){
        return null;
    }

    return null;
};

const toBundleRedirectUrl = (bundle) => {
    const metadataUrl = getBundleMetadataValue(bundle, 'redirect_url');
    const safeMetadataUrl = toSafeHttpUrl(metadataUrl);
    if(safeMetadataUrl){
        return safeMetadataUrl;
    }

    const directUrl = toSafeHttpUrl(bundle?.redirect_url ?? bundle?.redirectUrl);
    if(directUrl){
        return directUrl;
    }

    return null;
};

const toBundleDateLabel = (value) => {
    if(!value){
        return null;
    }
    const parsedDate = new Date(value);
    if(!Number.isFinite(parsedDate.getTime())){
        return null;
    }
    return getReadableDate(value);
};

const resolveOwnedBundles = ({ purchasedEntries = [], activeBundles = [] } = {}) => {
    const normalizedActiveBundles = Array.isArray(activeBundles) ? activeBundles : [];
    const normalizedPurchasedEntries = Array.isArray(purchasedEntries) ? purchasedEntries : [];

    const activeBundlesById = new Map(
        normalizedActiveBundles
            .map((bundle) => [Number(bundle?.id), bundle])
            .filter(([bundleId]) => Number.isFinite(bundleId))
    );
    const activeBundlesByName = new Map(
        normalizedActiveBundles
            .map((bundle) => [normalizeBundleToken(bundle?.name), bundle])
            .filter(([bundleName]) => !!bundleName)
    );

    const resolvePluginNames = (bundle) => {
        const seenPluginNames = new Set();
        return (Array.isArray(bundle?.apps) ? bundle.apps : [])
            .map((app) => String(app?.name ?? '').trim())
            .filter((name) => {
                if(!name){
                    return false;
                }
                const normalizedName = normalizeBundleToken(name);
                if(seenPluginNames.has(normalizedName)){
                    return false;
                }
                seenPluginNames.add(normalizedName);
                return true;
            });
    };

    const fromPurchasedEntries = normalizedPurchasedEntries
        .map((entry) => {
            const entryBundle = entry?.bundle && typeof entry.bundle === 'object' ? entry.bundle : null;
            const candidateBundleId = [
                entryBundle?.id,
                entry?.bundle_id,
                entry?.id
            ]
                .map((bundleId) => Number(bundleId))
                .find((bundleId) => Number.isFinite(bundleId));

            const candidateBundleName = String(entryBundle?.name ?? entry?.name ?? '').trim();
            const bundleFromActiveById = Number.isFinite(candidateBundleId)
                ? activeBundlesById.get(candidateBundleId)
                : null;
            const bundleFromActiveByName = candidateBundleName
                ? activeBundlesByName.get(normalizeBundleToken(candidateBundleName))
                : null;
            const resolvedBundle = entryBundle || bundleFromActiveById || bundleFromActiveByName || null;
            const bundleName = String(resolvedBundle?.name ?? candidateBundleName).trim();

            if(!bundleName){
                return null;
            }

            const resolvedBundleId = Number(resolvedBundle?.id);
            const isAssignedBundle = Boolean(
                entry?.is_assigned === true ||
                entry?.assigned_by ||
                entry?.assignment_id ||
                entry?.assignment?.id ||
                String(entry?.source ?? '').trim().toLowerCase() === 'assignment'
            );
            const accessDateLabel = toBundleDateLabel(
                entry?.assigned_at ||
                entry?.purchased_at ||
                entry?.created_at ||
                entry?.updated_at
            );
            const redirectUrl = toBundleRedirectUrl(resolvedBundle) || toSafeHttpUrl(entry?.redirect_url ?? entry?.redirectUrl);

            return {
                id: Number.isFinite(resolvedBundleId) ? resolvedBundleId : null,
                name: bundleName,
                accessLabel: isAssignedBundle ? 'Assigned' : 'Purchased',
                accessDateLabel,
                redirectUrl,
                pluginNames: resolvePluginNames(resolvedBundle)
            };
        })
        .filter((bundle) => !!bundle);

    const fromPurchasedActiveBundles = normalizedActiveBundles
        .filter((bundle) => bundle?.is_purchased === true)
        .map((bundle) => {
            const bundleName = String(bundle?.name ?? '').trim();
            if(!bundleName){
                return null;
            }
            const bundleId = Number(bundle?.id);
            return {
                id: Number.isFinite(bundleId) ? bundleId : null,
                name: bundleName,
                accessLabel: 'Purchased',
                accessDateLabel: null,
                redirectUrl: toBundleRedirectUrl(bundle),
                pluginNames: resolvePluginNames(bundle)
            };
        })
        .filter((bundle) => !!bundle);

    const dedupedBundlesMap = new Map();
    [...fromPurchasedEntries, ...fromPurchasedActiveBundles].forEach((bundle) => {
        const dedupeKey = Number.isFinite(bundle?.id)
            ? `id:${bundle.id}`
            : `name:${normalizeBundleToken(bundle?.name)}`;
        if(!dedupeKey){
            return;
        }
        const previousBundle = dedupedBundlesMap.get(dedupeKey);
        if(!previousBundle){
            dedupedBundlesMap.set(dedupeKey, bundle);
            return;
        }
        dedupedBundlesMap.set(dedupeKey, {
            ...previousBundle,
            accessLabel: previousBundle.accessLabel === 'Assigned' ? 'Assigned' : bundle.accessLabel,
            accessDateLabel: previousBundle.accessDateLabel || bundle.accessDateLabel || null,
            redirectUrl: previousBundle.redirectUrl || bundle.redirectUrl || null,
            pluginNames: previousBundle.pluginNames.length >= bundle.pluginNames.length
                ? previousBundle.pluginNames
                : bundle.pluginNames
        });
    });

    return [...dedupedBundlesMap.values()].sort((bundleA, bundleB) => bundleA.name.localeCompare(bundleB.name));
};

const collectAddonIdsFromArray = (value, { onlyActive = true } = {}) => {
    if(!Array.isArray(value)){
        return [];
    }
    return value
        .filter((item) => {
            if(typeof item === 'number'){
                return Number.isFinite(item);
            }
            if(!item || typeof item !== 'object'){
                return false;
            }
            if(!onlyActive){
                return true;
            }
            return item.is_active !== false && item.active !== false;
        })
        .map((item) => (
            typeof item === 'number'
                ? Number(item)
                : Number(item.id ?? item.addon_id ?? item.addon?.id)
        ))
        .filter((id, index, source) => Number.isFinite(id) && source.indexOf(id) === index);
};

const extractActiveAddonIds = (subscription) => {
    if(!subscription || typeof subscription !== 'object'){
        return [];
    }

    const candidateCollections = [
        subscription.addons,
        subscription.active_addons,
        subscription.subscription_addons,
        subscription.addon_subscriptions,
    ];

    return candidateCollections
        .flatMap((collection) => collectAddonIdsFromArray(collection, { onlyActive: true }))
        .filter((id, index, source) => source.indexOf(id) === index);
};

const collectAddonPriceIdsFromArray = (value, { onlyActive = true } = {}) => {
    if(!Array.isArray(value)){
        return [];
    }

    return value
        .filter((item) => {
            if(typeof item === 'number'){
                return Number.isFinite(item);
            }
            if(!item || typeof item !== 'object'){
                return false;
            }
            if(!onlyActive){
                return true;
            }
            return item.is_active !== false && item.active !== false;
        })
        .map((item) => (
            typeof item === 'number'
                ? Number(item)
                : Number(item.addon_price_id ?? item.price_id ?? item.price?.id ?? item.addon_price?.id)
        ))
        .filter((id, index, source) => Number.isFinite(id) && source.indexOf(id) === index);
};

const extractActiveAddonPriceIds = (subscription) => {
    if(!subscription || typeof subscription !== 'object'){
        return [];
    }

    const candidateCollections = [
        subscription.addons,
        subscription.active_addons,
        subscription.subscription_addons,
        subscription.addon_subscriptions,
    ];

    return candidateCollections
        .flatMap((collection) => collectAddonPriceIdsFromArray(collection, { onlyActive: true }))
        .filter((id, index, source) => source.indexOf(id) === index);
};

const getAddonApiErrorMessage = (error, fallbackMessage) => {
    if(typeof error?.message === 'string' && error.message.trim()){
        return error.message;
    }

    const nestedErrors = error?.errors;
    if(Array.isArray(nestedErrors) && nestedErrors.length > 0){
        const firstError = nestedErrors.find((item) => typeof item === 'string' && item.trim());
        if(firstError){
            return firstError;
        }
    }
    if(nestedErrors && typeof nestedErrors === 'object'){
        const firstFieldErrors = Object.values(nestedErrors).find((value) => Array.isArray(value) && value.length > 0);
        if(Array.isArray(firstFieldErrors) && typeof firstFieldErrors[0] === 'string'){
            return firstFieldErrors[0];
        }
    }

    if(typeof error?.error === 'string' && error.error.trim()){
        return error.error;
    }
    if(typeof error?.msg === 'string' && error.msg.trim()){
        return error.msg;
    }
    if(typeof error?.response?.data?.message === 'string' && error.response.data.message.trim()){
        return error.response.data.message;
    }
    if(typeof error?.response?.data?.error === 'string' && error.response.data.error.trim()){
        return error.response.data.error;
    }

    return fallbackMessage;
};

const Account = ({subscriptionSuccess=false}) => {
    //dummy padding

    //Custom Hooks
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {user, setUser, reloadCurrentUser, isAuthenticating, assetsAvailable} = useUserContext();
    const {marketplaceEnabled} = useMarketplaceExperiment();

    //Ref Hooks
    const headerRef = useRef(null);
    const personalInputsInitializedRef = useRef(false);
    const personalDataRef = {
        'first_name': useRef(null),
        'last_name': useRef(null),
        'username': useRef(null),
        'profession': useRef(null),
        'software_preferences': useRef(null),
        'usage_purpose': useRef(null),
        'website_channel': useRef(null),
        'how_hear': useRef(null),
        'next_desire': useRef(null)
    };

    const billingDataRef = {
        'business_name': useRef(null),
        'address': useRef(null),
        'vat': useRef(null),
        'invoice_notes': useRef(null)
    }
    const newPasswordRef = useRef(null);
    const repeatPasswordRef = useRef(null);
    const subscriptionUpgradeModalsRef = useRef(null);
    const mountedRef = useRef(true);
    const teamSeatsPollingRef = useRef(null);
    const teamSeatsToastTimeoutRef = useRef(null);
    const bundleCheckoutRefreshTriggeredRef = useRef(false);
    const cancelConfirmationPromiseRef = useRef(null);
    const cancelConfirmationTriggeredRef = useRef(null);

    //State Hooks

    const [premiumAssetsPolicy, setPremiumAssetsPolicy] = useState(false);
    const [planDownloadsPerDay, setPlanDownloadsPerDay] = useState(0);
    const [defaultProducts, setDefaultProducts] = useState(null);
    const selectedTab = resolveAccountTabFromPath(location.pathname);

    const [loadingGeneral, setLoadingGeneral] = useState(false);

    const [personalData, setPersonalData] = useState(null);
    const [profileDataErrors, setProfileDataErrors] = useState({});
    const [billingData, setBillingData] = useState(null);
    const [invoices, setInvoices] = useState([]);

    const [newsletter, setNewsletter] = useState(false);
    const [footagecrateBlender, setFootagecrateBlender] = useState(false);

    const [questionnaireModal, setQuestionnaireModal] = useState(false);
    const [subscriptionExpand, setSubscriptionExpand] = useState(true);
    const [cancelConfirmModal, setCancelConfirmModal] = useState(false);
    const [cancelSuccessModal, setCancelSuccessModal] = useState(false);
    const [cancelConfirmationState, setCancelConfirmationState] = useState('idle');
    const [cancelConfirmationSubscriptionId, setCancelConfirmationSubscriptionId] = useState(null);
    const [resumeSuccessModal, setResumeSuccessModal] = useState(false);
    const [pauseSuccessModal, setPauseSuccessModal] = useState(false);
    const [pauseConfigModal, setPauseConfigModal] = useState(false);
    const [pauseMode, setPauseMode] = useState('indefinite'); // indefinite | days | date
    const [pauseResumesInDays, setPauseResumesInDays] = useState(30);
    const [pauseResumesAt, setPauseResumesAt] = useState('');
    const [pauseError, setPauseError] = useState(null);
    const [paymentMethodError, setPaymentMethodError] = useState(null);
    const [openingPaymentMethod, setOpeningPaymentMethod] = useState(false);
    const [applyPromoModal, setApplyPromoModal] = useState(false);

    const [passwordError, setPasswordError] = useState(null);
    const [currentPassword, setCurrentPassword] = useState(null);
    const [repeatPassword, setRepeatPassword] = useState(null);
    const [newPassword, setNewPassword] = useState(null);
    const [passwordChangeSuccessModal, setPasswordChangeSuccessModal] = useState(false);


    const [teamMembers, setTeamMembers] = useState([]);
    const [teamMembersLoading, setTeamMembersLoading] = useState(false);
    const [teamAppLicenses, setTeamAppLicenses] = useState([]);
    const [teamBundleMemberCapacity, setTeamBundleMemberCapacity] = useState(0);
    const [teamInvites, setTeamInvites] = useState([]);
    const [teamInvitesLoading, setTeamInvitesLoading] = useState(false);
    const [teamInvitesEmail, setTeamInvitesEmail] = useState(null);
    const [inviteError, setInviteError] = useState(null);
    const [inviteSuccess, setInviteSuccess] = useState(null);
    const [sendingInvite, setSendingInvite] = useState(false);
    const [resendingInviteIds, setResendingInviteIds] = useState({});
    const [upgradeEnterprisePlans, setUpgradeEnterprisePlans] = useState([]);
    const [teamSeatsData, setTeamSeatsData] = useState(null);
    const [teamSeatsLoading, setTeamSeatsLoading] = useState(false);
    const [teamSeatsError, setTeamSeatsError] = useState(null);
    const [canRevokeSeats, setCanRevokeSeats] = useState(true);
    const [revokingSeatIds, setRevokingSeatIds] = useState({});
    const [teamSeatsToast, setTeamSeatsToast] = useState(null);
    const [seatTickerMs, setSeatTickerMs] = useState(() => Date.now());
    const [currentPlanAddons, setCurrentPlanAddons] = useState([]);
    const [currentPlanAddonsLoading, setCurrentPlanAddonsLoading] = useState(false);
    const [currentPlanAddonsError, setCurrentPlanAddonsError] = useState(null);
    const [subscribingAddonPriceIds, setSubscribingAddonPriceIds] = useState({});
    const [locallyActivatedAddonPriceIds, setLocallyActivatedAddonPriceIds] = useState([]);
    const [ownedBundles, setOwnedBundles] = useState([]);
    const [ownedBundlesLoading, setOwnedBundlesLoading] = useState(false);
    const [ownedBundlesError, setOwnedBundlesError] = useState(null);
    const [purchasedAssets, setPurchasedAssets] = useState([]);
    const [purchasedAssetsLoading, setPurchasedAssetsLoading] = useState(false);
    const [purchasedAssetsError, setPurchasedAssetsError] = useState(null);
    const [assetOrders, setAssetOrders] = useState([]);
    const [assetOrdersLoading, setAssetOrdersLoading] = useState(false);
    const [assetOrdersError, setAssetOrdersError] = useState(null);


    const [mobileVisible, setMobileVisible] = useState(false);
    const bundleCheckoutStatus = useMemo(() => (
        normalizeBundleCheckoutStatus(searchParams.get('bundle_checkout'))
    ), [searchParams]);
    const cancellationConfirmationStatus = useMemo(() => (
        isCancellationConfirmationStatus(searchParams.get('status'))
    ), [searchParams]);
    const showBundleCheckoutMessage = bundleCheckoutStatus === 'success' || bundleCheckoutStatus === 'cancelled';
    const planFeatures = user?.team?.subscription?.plan?.features;
    const visibleTeamSeatContexts = useMemo(() => TEAM_SEAT_CONTEXTS.filter((context) => (
        hasTeamSeatContextFeatureEnabled(planFeatures, context.key)
    )), [planFeatures]);
    
    //UseEffect Hooks
    useEffect(() => {
        let isCancelled = false;

        if(bundleCheckoutStatus !== 'success'){
            bundleCheckoutRefreshTriggeredRef.current = false;
            return () => {
                isCancelled = true;
            };
        }

        if(bundleCheckoutRefreshTriggeredRef.current){
            return () => {
                isCancelled = true;
            };
        }
        bundleCheckoutRefreshTriggeredRef.current = true;

        const refreshTokenAfterBundleCheckout = async () => {
            let refreshTokenFn = typeof window.refreshToken === 'function'
                ? window.refreshToken
                : null;

            if(!refreshTokenFn){
                for (let attempt = 0; attempt < 20; attempt += 1) {
                    if(isCancelled){
                        return;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 200));
                    if(typeof window.refreshToken === 'function'){
                        refreshTokenFn = window.refreshToken;
                        break;
                    }
                }
            }

            if(typeof refreshTokenFn !== 'function'){
                return;
            }

            try{
                await refreshTokenFn();
                if(typeof reloadCurrentUser === 'function'){
                    await reloadCurrentUser();
                }
            } catch (error) {
                console.log("error while refreshing token after bundle checkout success", error);
            }
        };

        refreshTokenAfterBundleCheckout();

        return () => {
            isCancelled = true;
        };
    }, [bundleCheckoutStatus, reloadCurrentUser]);

    useEffect(() => {
        //console.log("SETUSER", setUser);
        if(!user && !isAuthenticating){
            //console.log("No user logged in on member page", user);
            navigate(`/login/`, { replace: true });
            return;
        }
           
        if(isAuthenticating){
            setLoadingGeneral(true);
            return;
        }
        setLoadingGeneral(false);


        getProducts({campaign: false}).then(products => {
            //console.log("MY PRODUCTS", {products});
            setDefaultProducts(products);
        })
        .catch(e => {
            console.log("error getting products", e, {campaign: false});
        });

        if(!personalInputsInitializedRef.current){
            ['first_name', 'last_name', 'username'].forEach(key => {
                if(personalDataRef[key].current && personalDataRef[key].current.setValue){
                    personalDataRef[key].current.setValue(user[key] ?? '');
                }
            });

            Object.keys(user.settings?user.settings:{}).forEach(key => {
                if(['profession','software_preferences', 'usage_purpose'].includes(key)){
                    if(personalDataRef[key].current && personalDataRef[key].current.setValue){
                        if (Array.isArray(user.settings[key])) {
                            personalDataRef[key].current.setValue(user.settings[key][0]);
                        } else {
                            personalDataRef[key].current.setValue(user.settings[key]?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
                        }
                    }
                }
                if(['website_channel', 'how_hear', 'next_desire'].includes(key)){
                    if(personalDataRef[key].current && personalDataRef[key].current.setValue){
                        personalDataRef[key].current.setValue(user.settings[key]);
                    }
                }
                if(key === 'newsletter'){
                    setNewsletter(user.settings[key]);
                }
                if(key === 'footagecrateBlenderAddon'){
                    setFootagecrateBlender(user.settings[key]);
                }
            });

            personalInputsInitializedRef.current = true;
        }

        if(user.team && user.team.subscription && user.team.subscription.plan){
            //If there was a susbcriptio success inform to analytics
            if(subscriptionSuccess){
                //console.log("Subscription success");
                setLoadingGeneral(true);
                try{
                    //Analytics.goalPlan(user.team.subscription.plan);
                    Analytics.conversion(user.team.subscription.plan.price, user.team.subscription.id);
                }catch(e){
                    console.log("Problem with analytics event", e);
                }

                setTimeout(() => {
                    try{
                        document.location.href='/account';
                    }catch(e){
                        console.log("error navigating", e);
                    }
                    navigate('/account', { replace: true });
                }, 1000); 
            }

            //console.log("USER ON MEMBER", user);
            setPremiumAssetsPolicy(Boolean(user.premiumAssetsPolicy) || hasPremiumAssetsAccess(user.team.subscription.plan));
            setPlanDownloadsPerDay(user.assetsPerDay);

        }
    }, [user, isAuthenticating]);


    //reset invite error and success messages when email changes
    useEffect(() => {
        setInviteError(null);
        setInviteSuccess(null);
    }, [teamInvitesEmail]);

    useEffect(() => {
        if(selectedTab === "subscription") {
            getInvoices().then(data =>  {
                //console.log("invoices", data);
                setInvoices(data);
            });
            teamFn.settings().then(team => {
                //console.log("team", team);
                
                Object.keys(team?team:{}).forEach(key => {
                    if(['business_name', 'address', 'vat', 'invoice_notes'].includes(key)){
                        if(billingDataRef[key].current && billingDataRef[key].current.setValue){
                            billingDataRef[key].current.setValue(team[key]);
                        }
                    }
                });
            });
        }else if(selectedTab === 'team' || selectedTab === 'licenses') {
            fetchTeamAndInvites();
            if(!user?.is_team_owner){
                return;
            }
            getProducts({campaign: 'enterprise-plus'}).then(upgradeProducts => {
                if(upgradeProducts && upgradeProducts.length){
                    const upgradeProduct = upgradeProducts[0];

                    const upgradePlans = upgradeProduct.plans.filter(plan => {
                        return (
                                plan.periodicity_type === user.team.subscription.plan.periodicity_type &&
                                plan.id != user.team.subscription.plan.id && 
                                plan.price > user.team.subscription.plan.price
                        );
                    });
                    //console.log("UPGRADE PLANS", upgradePlans);
                    setUpgradeEnterprisePlans(upgradePlans);
                } 
            })
            .catch(e => {
                console.debug("error getting products", e, {campaign: 'enterprise-plus'});
            });
        }
    }, [selectedTab, user?.is_team_owner]);

    useEffect(() => {
        if(user && !user.is_team_owner && ['subscription', 'team'].includes(selectedTab)){
            navigate(`${ACCOUNT_SECTION_PATHS.licenses}${location.search}`, {replace: true});
        }
    }, [location.search, navigate, selectedTab, user]);

    useEffect(() => {
        console.log("PERSONAL DATA > ", personalData)
    }, [personalData]);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            if(teamSeatsPollingRef.current){
                clearInterval(teamSeatsPollingRef.current);
                teamSeatsPollingRef.current = null;
            }
            if(teamSeatsToastTimeoutRef.current){
                clearTimeout(teamSeatsToastTimeoutRef.current);
                teamSeatsToastTimeoutRef.current = null;
            }
        };
    }, []);
    
    //Custom Handler/Methods/Functions

    const showTeamSeatsToast = useCallback((message) => {
        if(!message){
            return;
        }
        setTeamSeatsToast(message);
        if(teamSeatsToastTimeoutRef.current){
            clearTimeout(teamSeatsToastTimeoutRef.current);
        }
        teamSeatsToastTimeoutRef.current = setTimeout(() => {
            if(!mountedRef.current){
                return;
            }
            setTeamSeatsToast(null);
            teamSeatsToastTimeoutRef.current = null;
        }, 3000);
    }, []);

    const clearTeamSeatsPolling = useCallback(() => {
        if(teamSeatsPollingRef.current){
            clearInterval(teamSeatsPollingRef.current);
            teamSeatsPollingRef.current = null;
        }
    }, []);

    const fetchTeamSeats = useCallback(async ({showLoading = false} = {}) => {
        if(!user?.is_team_owner){
            return;
        }

        if(showLoading && mountedRef.current){
            setTeamSeatsLoading(true);
        }
        if(mountedRef.current){
            setTeamSeatsError(null);
        }

        try{
            const seats = await teamFn.seats();
            if(!mountedRef.current){
                return;
            }
            const normalizedSeats = normalizeTeamSeatsPayload(seats);
            if(!normalizedSeats){
                setTeamSeatsData(null);
                setTeamSeatsError('Unable to load team seats usage.');
                return;
            }
            setTeamSeatsData(normalizedSeats);
            setCanRevokeSeats(true);
        }catch(error){
            if(!mountedRef.current){
                return;
            }

            const statusCode = error?.response?.status;
            const defaultMessage = error?.response?.data?.message || error?.message;
            const errorMessage = getTeamSeatsErrorMessage(statusCode, defaultMessage);
            setTeamSeatsData(null);
            setTeamSeatsError(errorMessage);
            if(statusCode === 403){
                setCanRevokeSeats(false);
            }
        }finally{
            if(showLoading && mountedRef.current){
                setTeamSeatsLoading(false);
            }
        }
    }, [user?.is_team_owner]);

    useEffect(() => {
        const hasSeatsFeature = hasTeamSeatFeatureEnabled(user?.team?.subscription?.plan?.features);
        const shouldPollTeamSeats = selectedTab === 'team' && !!user?.is_team_owner && hasSeatsFeature;
        clearTeamSeatsPolling();

        if(!shouldPollTeamSeats){
            return;
        }

        fetchTeamSeats({showLoading: true});

        const startPolling = () => {
            clearTeamSeatsPolling();
            if(document.hidden){
                return;
            }

            teamSeatsPollingRef.current = setInterval(() => {
                fetchTeamSeats();
            }, 10000);
        };

        const handleVisibilityChange = () => {
            if(document.hidden){
                clearTeamSeatsPolling();
                return;
            }

            fetchTeamSeats();
            startPolling();
        };

        startPolling();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearTeamSeatsPolling();
        };
    }, [clearTeamSeatsPolling, fetchTeamSeats, selectedTab, user?.is_team_owner, user?.team?.subscription?.plan?.features]);

    useEffect(() => {
        const hasActiveSeatSessions =
            selectedTab === 'team' &&
            !!user?.is_team_owner &&
            !teamSeatsLoading &&
            !teamSeatsError &&
            visibleTeamSeatContexts.some((context) => Array.isArray(teamSeatsData?.[context.key]?.seats) && teamSeatsData[context.key].seats.length > 0);

        if(!hasActiveSeatSessions){
            return;
        }

        const updateTicker = () => {
            setSeatTickerMs(Date.now());
        };

        let intervalId = null;
        const stopTicker = () => {
            if(intervalId){
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const startTicker = () => {
            stopTicker();
            if(document.hidden){
                return;
            }

            updateTicker();
            intervalId = setInterval(updateTicker, 1000);
        };

        const handleVisibilityChange = () => {
            if(document.hidden){
                stopTicker();
                return;
            }
            startTicker();
        };

        startTicker();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopTicker();
        };
    }, [selectedTab, user?.is_team_owner, teamSeatsLoading, teamSeatsError, teamSeatsData, visibleTeamSeatContexts]);

    const handleChangeRepeatPassword = (val) => {
        setRepeatPassword(val);
    };
    const handleChangeNewPassword = (val) => {
        setNewPassword(val);
    };
    const handleNewPasswordSave = async () => {
        //console.log("handleNewPasswordSave",newPassword, repeatPassword);
        let passwordSaveError=false;
        setPasswordError(null);
        if(!newPassword && !repeatPassword){
            setPasswordError("Passwords cant't be empty");
            return;
        }
        if(newPassword !== repeatPassword){
            setPasswordError("Passwords don't match", newPassword, repeatPassword);
            
            return;
        }

        setLoadingGeneral(true);
        
        let data = {
            password: newPassword,
        };
        try{
            await userFn(data);
        } catch(error){ 
            //console.log("error", error.errors.password);
            if(error.errors && error.errors && error.errors.password && error.errors.password.length){
                setPasswordError(error.errors.password);
            } else{
                setPasswordError(error.message);
            }

            passwordSaveError=true;
        }

        setNewPassword(null);
        newPasswordRef.current.setValue('');
        setRepeatPassword(null);
        repeatPasswordRef.current.setValue('');
        
        setLoadingGeneral(false);
        //console.log("Save password result", passwordSaveError);
        setPasswordChangeSuccessModal(!passwordSaveError);
    };

    const handleProfileData = async (field, value) => {
        const nextValue = String(value ?? '').trim();
        const currentValue = String(user?.[field] ?? '').trim();
        const fieldLabel = PROFILE_FIELD_LABELS[field] || 'Profile field';

        setProfileDataErrors(prev => ({
            ...prev,
            [field]: null
        }));

        if(!nextValue){
            if(!currentValue){
                return;
            }
            const errorMessage = `${fieldLabel} can't be empty.`;
            setProfileDataErrors(prev => ({
                ...prev,
                [field]: errorMessage
            }));
            throw new Error(errorMessage);
        }

        if(nextValue === currentValue){
            return;
        }

        try{
            await updateUserProfileFn({[field]: nextValue});
            setUser(prev => {
                if(!prev){
                    return prev;
                }
                return {
                    ...prev,
                    [field]: nextValue
                };
            });
            return nextValue;
        }catch(error){
            setProfileDataErrors(prev => ({
                ...prev,
                [field]: getProfileFieldErrorMessage(error, field)
            }));
            throw error;
        }
    };

    const handlePersonalData = async (val) => {
        const newData = { ...personalData, ...val };
        const filteredData = Object.fromEntries(
            Object.entries(newData).filter(([key]) =>
                ['profession', 'software_preferences', 'usage_purpose', 'website_channel', 'how_hear', 'next_desire'].includes(key)
            )
        );
        try {
            await meFn(filteredData);
            const refreshedUser = await userFn();
            if(!refreshedUser){
                throw new Error('Personal information could not be verified after saving.');
            }
            const valuesWereSaved = Object.entries(val).every(([key, value]) => (
                String(getPersonalInformationValue(refreshedUser, key) ?? '') === String(value ?? '')
            ));
            if(!valuesWereSaved){
                throw new Error('Personal information could not be verified after saving.');
            }
            setUser(prev => prev ? {...prev, settings: refreshedUser.settings ?? prev.settings} : prev);
            setPersonalData(newData);
            return newData;
        } catch (e) {
            console.error("Error saving personal data", e);
            throw e;
        }
    };

    const handleSavePersonalData = async () => {

        setLoadingGeneral(true);
        
        let data = {};
        Object.keys(personalData).forEach(key => {
            
            if(['profession','software_preferences', 'usage_purpose', 'website_channel', 'how_hear', 'next_desire'].includes(key)){
                data[key] = personalData[key];
            }
            
        });
        try{
            await meFn(data);
        }catch(e){
            console.log("ERROR SAVING PERSONAL DATA", e);
        }
        reloadCurrentUser();
        setLoadingGeneral(false);
    };

    const handleBillingChange = (val) => {
        setBillingData(prev => {
            let newData = {...prev, ...val};
            return newData;
        });
    };

    const handleSaveBillingData = async () => {

        setLoadingGeneral(true);
        
        let data = {};
        Object.keys(billingData).forEach(key => {
            
            if(['business_name', 'address', 'vat', 'invoice_notes'].includes(key)){
                data[key] = billingData[key];
            }
            
        });
        await teamFn.settings(data);
        setLoadingGeneral(false);
    };
    

    const handleSubscribeNewsletter = async (useGeneralLoading=true) => {
        if(useGeneralLoading){
            setLoadingGeneral(true);
        }
        try{
            await meFn({newsletter:true});
        }catch(e){
            console.log("ERROR SUBSCRIBING NEWSLETTER", e);
        }
        setNewsletter(true);
        if(useGeneralLoading){
            setLoadingGeneral(false);
        }
    }
    const handleUnsubscribeNewsletter = async (useGeneralLoading) => {
        if(useGeneralLoading){
            setLoadingGeneral(true);
        }
        try{
            await meFn({newsletter:false});
        }catch(e){
            console.log("ERROR UNSUBSCRIBING NEWSLETTER", e);
        }
        setNewsletter(false);
        if(useGeneralLoading){
            setLoadingGeneral(false);
        }
    };

    const handleEnableFootagecrateBlender = async () => {
        //setLoadingGeneral(true);
        try{
            await meFn({footagecrateBlenderAddon:true});
        }catch(e){
            console.log("ERROR ACTIVATING FOOTAGE BLENDER WEB LINK", e);
        }
        setFootagecrateBlender(true);
        //setLoadingGeneral(false);
    }
    const handleDisableFootagecrateBlender = async () => {
        //setLoadingGeneral(true);
        try{
            await meFn({footagecrateBlenderAddon:false});
        }catch(e){
            console.log("ERROR DISABLING FOOTAGE BLENDER WEB LINK", e);
        }
        setFootagecrateBlender(false);
        //setLoadingGeneral(false);
    };





    const handleUpgradeClick = option => {
        let provider = null;
        switch(option){
            case 'register':
                navigate('/register', { replace: true });
            break;
            case 'paypal':
                provider = 'paypal'
            break;
            case 'stripe':
                provider = 'stripe'
            break;
        }

        
        if(defaultProducts && defaultProducts.length){
            let selectedPlan = defaultProducts[1].plans.find(e => e.periodicity_type==='Month' && e.price>0);
            let postData = {provider, planId: selectedPlan.id};
            subscribeUserFn(postData).then(data => {
                if(data.url){
                    if (data.type === 'checkout' || data.type === 'approval') trackGa4Checkout(data.analytics);
                    document.location.href = data.url;
                }
            })
            .catch(e => {
                console.log("ERROR SUBSCRIBING", e);
            });
        }
        
    }

    const handleCancelSubscription = () => {
        
        setLoadingGeneral(true);
        setCancelConfirmModal(false);
        cancelSubscription().then(async data => {
            //console.log("Subscription cancelled", data);
            setTimeout(async () => {
                await reloadCurrentUser();
                setLoadingGeneral(false);
                setCancelSuccessModal(true);
            }, 1500);
            
        })
        .catch(e => {
            console.log("cancel subscription problem", e);
        });
    };

    const handleDeleteMe = () => {
        if(!window.confirm(`Are you sure you want to delete your user and all of it's data from productioncrate databases? This can not be undone.`)){
            return;
        }
        setLoadingGeneral(true);
        deleteUserFn().then(async data => {
            //console.log("User deleted", data);
            setLoadingGeneral(false);
            navigate("/register");
        });
    };

    const fetchTeamAndInvites = async (memberCallback, invitationCallback) => {
        setTeamMembersLoading(true);
        const [membersResult, appLicensesResult] = await Promise.allSettled([
            teamFn.members.list(),
            teamFn.appLicenses.list(),
        ]);
        const members = membersResult.status === 'fulfilled' && Array.isArray(membersResult.value)
            ? membersResult.value
            : [];
        const appLicenses = appLicensesResult.status === 'fulfilled'
            ? getTeamLicenseCollection(appLicensesResult.value)
            : [];
        const bundleMemberCapacity = appLicensesResult.status === 'fulfilled'
            ? getBundleMemberCapacity(appLicensesResult.value)
            : 0;

        setTeamMembers(members);
        setTeamAppLicenses(appLicenses);
        setTeamBundleMemberCapacity(bundleMemberCapacity);
        if(memberCallback){
            memberCallback();
        }
        setTeamMembersLoading(false);

        if(!user?.is_team_owner || getTeamMemberLimit(user, bundleMemberCapacity) <= 1){
            setTeamInvites([]);
            setTeamInvitesLoading(false);
            return;
        }
        setTeamInvitesLoading(true);
        try {
            const invites = await teamFn.invitations.list();
            setTeamInvites(invites);
            if(invitationCallback){
                invitationCallback();
            }
        } finally {
            setTeamInvitesLoading(false);
        }
    };
    const handleMemberDelete = (index, id) => {
        const member = teamMembers[index];
        if(!canRemoveTeamMember(member, user)){
            return;
        }
        setTeamMembers(prev => {
            let newMembers = [...prev];
            newMembers[index].isDeleting = true;
            return newMembers;
        });
        teamFn.members.delete(id).then(data => {
            //console.log("MEMBER DELETE", data);
            fetchTeamAndInvites();
        });
    };

    const handleInviteDelete = (id) => {
        if(!canInviteTeamMembers(user, teamBundleMemberCapacity)){
            return;
        }
        setTeamInvites(currentInvites => currentInvites.filter(invite => invite.id !== id));
        teamFn.invitations.delete(id).then(data => {
            //console.log("INVITE DELETE", data);
            fetchTeamAndInvites();
        });
    };
    const handleInviteEmail = () => {
        if(!canInviteTeamMembers(user, teamBundleMemberCapacity)){
            return;
        }
        setInviteError(null);
        setInviteSuccess(null);
        setSendingInvite(true);

        if(!teamInvitesEmail){
            setSendingInvite(false);
            setInviteError("Email field can't be empty");
            return;
        }

        teamFn.invitations.invite(teamInvitesEmail).then(data => {
            //console.log("INVITE", data);
            fetchTeamAndInvites(null, () => {setSendingInvite(false);setInviteSuccess(data.message);});
        }).catch(error => {
            console.log("ERROR INVITE", error);
            setSendingInvite(false);
            setInviteError(error.message);
            
        });
        
    };

    const handleInviteResend = async (id) => {
        if(!canInviteTeamMembers(user, teamBundleMemberCapacity) || !id || resendingInviteIds[id]){
            return;
        }

        setInviteError(null);
        setInviteSuccess(null);
        setResendingInviteIds(current => ({...current, [id]: true}));

        try{
            const data = await teamFn.invitations.resend(id);
            await fetchTeamAndInvites();
            setInviteSuccess(data?.message || 'Invitation resent successfully.');
        }catch(error){
            setInviteError(error.message);
        }finally{
            setResendingInviteIds(current => {
                const next = {...current};
                delete next[id];
                return next;
            });
        }
    };

    const upgradeEnterprise = (newPlan) => {
        if(!subscriptionUpgradeModalsRef.current){
            return;
        }
        subscriptionUpgradeModalsRef.current.confirmUpgrade({
            planId: newPlan.id,
            fromPrice: user.team.subscription.plan.price,
            toPrice: newPlan.price,
            periodicityType: newPlan.periodicity_type,
        });
    };

    const questionnaireCallback = () => {

        if (user.team.subscription.plan.periodicity_type.toLowerCase() === 'month' && +user.team.subscription.plan.price === 49 && user.team.subscription.stripe) {
            setApplyPromoModal(true);
            return;
        }
        setCancelConfirmModal(true);
    }

    const applyPromoSubscribe = () => {
        setApplyPromoModal(false);
        if(!subscriptionUpgradeModalsRef.current || !defaultProducts){
            return;
        }
        let proProduct = defaultProducts.filter(pr => pr.name.toLowerCase() === 'pro');
        if(!proProduct || !proProduct.length){
            return;
        }
        let discountedPlan = proProduct[0].plans.find(e => e.periodicity_type==='Month' && e.price===39);
        if(!discountedPlan){
            return;
        }

        subscriptionUpgradeModalsRef.current.startUpgrade(discountedPlan.id);
    }


    const handleExpireAllSessions = async () => {
        setLoadingGeneral(true);
        await expireAllSessions();
        setLoadingGeneral(false);   
        document.location.href = '/account';
    }

    const handleTabSelect = (tab) => {
        const nextPath = ACCOUNT_SECTION_PATHS[tab] || ACCOUNT_SECTION_PATHS.settings;
        const nextSearch = searchParams.toString();
        navigate(`${nextPath}${nextSearch ? `?${nextSearch}` : ''}`);
        setMobileVisible(false);
        if(tab === 'subscription'){
            setSubscriptionExpand(true);
        }
    };

    const confirmCancellation = useCallback(async (subscriptionId) => {
        if (!subscriptionId) {
            setCancelConfirmationState('error');
            return false;
        }

        if (cancelConfirmationPromiseRef.current) {
            return cancelConfirmationPromiseRef.current;
        }

        setCancelConfirmationSubscriptionId(subscriptionId);
        setCancelConfirmationState('checking');

        const confirmationPromise = (async () => {
            for (let attempt = 0; attempt < 3; attempt += 1) {
                try {
                    const response = await withCancellationCheckTimeout(
                        confirmSubscriptionCancellation(subscriptionId),
                        5000,
                    );

                    if (response?.status === 'confirmed') {
                        await reloadCurrentUser();
                        navigate(ACCOUNT_SECTION_PATHS.subscription, {replace: true});
                        if (mountedRef.current) {
                            setCancelConfirmationState('idle');
                            setCancelConfirmationSubscriptionId(null);
                            setCancelSuccessModal(true);
                        }
                        return true;
                    }
                } catch (error) {
                    console.log('error confirming subscription cancellation', error);
                }

                if (attempt < 2) {
                    await waitForCancellationCheck(2000);
                }
            }

            if (mountedRef.current) {
                setCancelConfirmationState('error');
            }
            return false;
        })();

        cancelConfirmationPromiseRef.current = confirmationPromise;
        try {
            return await confirmationPromise;
        } finally {
            if (cancelConfirmationPromiseRef.current === confirmationPromise) {
                cancelConfirmationPromiseRef.current = null;
            }
        }
    }, [navigate, reloadCurrentUser]);

    useEffect(() => {
        const subscriptionId = user?.team?.subscription?.id;

        if (!cancellationConfirmationStatus || !subscriptionId
            || cancelConfirmationTriggeredRef.current === subscriptionId) {
            return;
        }

        cancelConfirmationTriggeredRef.current = subscriptionId;
        confirmCancellation(subscriptionId);
    }, [cancellationConfirmationStatus, confirmCancellation, user?.team?.subscription?.id]);

    const handleRetryCancellationConfirmation = () => {
        if (cancelConfirmationSubscriptionId) {
            confirmCancellation(cancelConfirmationSubscriptionId);
        }
    };

    const goToCancellationConfirmation = () => {
        navigate(`${ACCOUNT_SECTION_PATHS.subscription}?status=cancelled`);
    };

    const handleCancelClick = () => {
        if ( window.churnkey && user?.team?.subscription?.stripe && user?.team?.subscription?.churnkey){
            window.churnkey.init('show', {
                subscriptionId: user.team.subscription.churnkey.stripe_subscription_id,
                customerId: user.team.subscription.churnkey.stripe_customer_id,
                authHash: user.team.subscription.churnkey.user_hash,
                appId: '4yeidb989',
                mode: getEnvironment() === 'production'?'live':'test',
                provider: 'stripe',
                record: true,
                report: true,
                onGoToAccount: () => {
                    goToCancellationConfirmation();
                },
                onClose: (sessionResults) => {
                    console.log("Churnkey modal closed", sessionResults);
                    if (isChurnkeyCancellationResult(sessionResults)) {
                        goToCancellationConfirmation();
                    }
                }
            });
            return;
        }
        setQuestionnaireModal(true);
    };

    const applySubscriptionTeamUpdate = (data) => {
        if(!data || !data.team){
            reloadCurrentUser();
            return;
        }
        setUser(prev => {
            if(!prev){
                return prev;
            }
            const prevTeam = prev.team ?? {};
            return {
                ...prev,
                team: {
                    ...prevTeam,
                    subscription: data.team.subscription ?? prevTeam.subscription,
                    last_paid_subscription: data.team.last_paid_subscription ?? prevTeam.last_paid_subscription,
                }
            };
        });
    };

    /* Pause is currently initiated from Churnkey through Cancel Subscription.
    const handlePauseClick = () => {
        setPauseError(null);
        setPauseMode('indefinite');
        setPauseResumesInDays(30);
        setPauseResumesAt('');
        setPauseConfigModal(true);
    };
    */

    const handleUpdatePaymentMethod = async () => {
        if(openingPaymentMethod){
            return;
        }

        setPaymentMethodError(null);
        const paymentTab = window.open('about:blank', '_blank');
        if(!paymentTab){
            setPaymentMethodError('Could not open the payment page. Please allow pop-ups and try again.');
            return;
        }

        paymentTab.opener = null;
        setOpeningPaymentMethod(true);
        try{
            const data = await createStripePaymentMethodUpdateLink();
            if(!data?.url){
                throw new Error('Missing payment method update URL.');
            }
            paymentTab.location.replace(data.url);
        }catch(error){
            paymentTab.close();
            setPaymentMethodError('Could not open the payment page. Please try again.');
        }finally{
            setOpeningPaymentMethod(false);
        }
    };

    const handlePauseSubscription = async () => {
        setPauseError(null);

        const subscription = user?.team?.subscription;
        if(!subscription || subscription.paypal === true || subscription.stripe !== true){
            setPauseError("La pausa de suscripción solo está disponible para suscripciones Stripe");
            return;
        }

        let payload;
        if(pauseMode === 'days'){
            const days = parseInt(pauseResumesInDays, 10);
            if(!Number.isFinite(days) || days < 1){
                setPauseError("Ingresá un número de días válido (>= 1).");
                return;
            }
            payload = { resumes_in_days: days };
        }
        if(pauseMode === 'date'){
            if(!pauseResumesAt){
                setPauseError("Elegí una fecha/hora futura.");
                return;
            }
            const ms = new Date(pauseResumesAt).getTime();
            if(!Number.isFinite(ms)){
                setPauseError("Elegí una fecha/hora válida.");
                return;
            }
            if(ms <= Date.now()){
                setPauseError("La fecha debe ser futura.");
                return;
            }
            payload = { resumes_at: ms };
        }

        setLoadingGeneral(true);
        try{
            const data = await pauseSubscriptionFn(payload);
            if(data && data.ok){
                applySubscriptionTeamUpdate(data);
                setPauseConfigModal(false);
                setPauseSuccessModal(true);
                return;
            }
            setPauseError("La pausa de suscripción solo está disponible para suscripciones Stripe");
        }catch(e){
            console.log("pause subscription problem", e);
            setPauseError("La pausa de suscripción solo está disponible para suscripciones Stripe");
        }finally{
            setLoadingGeneral(false);
        }
    };

    const handleResumeSubscription = async () => {
        const subscription = user?.team?.subscription;
        const provider = subscription?.stripe ? 'stripe' : (subscription?.paypal ? 'paypal' : null);
        const planId = subscription?.plan?.id ?? user?.team?.last_paid_subscription?.plan?.id;
        if(!provider || !planId){
            return;
        }

        setLoadingGeneral(true);
        try{
            const data = await resumeSubscriptionFn({provider, planId});
            if(data && data.url){
                document.location.href = data.url;
                return;
            }
            if(data && data.team){
                applySubscriptionTeamUpdate(data);
            }else{
                reloadCurrentUser();
            }
            setResumeSuccessModal(true);
        }catch(e){
            console.log("resume subscription problem", e);
        }finally{
            setLoadingGeneral(false);
        }
    };

    const handleRetryTeamSeats = () => {
        fetchTeamSeats({showLoading: true});
    };

    const currentSubscription = user?.team?.subscription ?? null;
    const currentSubscriptionPlanId = currentSubscription?.plan?.id ?? null;
    const canManagePlanAddons = Boolean(
        currentSubscription &&
        currentSubscription.stripe === true &&
        currentSubscription.paypal !== true &&
        !isSubscriptionPaused(currentSubscription) &&
        !isSubscriptionCancelled(currentSubscription) &&
        currentSubscriptionPlanId
    );
    const activeSubscriptionAddonIds = useMemo(
        () => extractActiveAddonIds(currentSubscription),
        [currentSubscription]
    );
    const activeSubscriptionAddonPriceIds = useMemo(
        () => extractActiveAddonPriceIds(currentSubscription),
        [currentSubscription]
    );
    const activeAddonIdsSet = useMemo(
        () => new Set(activeSubscriptionAddonIds),
        [activeSubscriptionAddonIds]
    );
    const activeAddonPriceIdsSet = useMemo(
        () => new Set([...activeSubscriptionAddonPriceIds, ...locallyActivatedAddonPriceIds]),
        [activeSubscriptionAddonPriceIds, locallyActivatedAddonPriceIds]
    );
    const shouldRenderPlanAddonsSection = !!user?.is_team_owner && canManagePlanAddons && (
        currentPlanAddonsLoading ||
        !!currentPlanAddonsError ||
        currentPlanAddons.length > 0
    );

    const loadCurrentPlanAddons = useCallback(async () => {
        if(!canManagePlanAddons || !currentSubscriptionPlanId){
            setCurrentPlanAddons([]);
            setCurrentPlanAddonsLoading(false);
            setCurrentPlanAddonsError(null);
            return;
        }

        setCurrentPlanAddonsLoading(true);
        setCurrentPlanAddonsError(null);
        try{
            const addons = await getPlanAddonsFn(currentSubscriptionPlanId);
            const availableAddons = Array.isArray(addons)
                ? addons.filter((addon) => (
                    addon &&
                    addon.is_active !== false &&
                    Number.isFinite(getAddonPriceId(addon))
                ))
                : [];
            setCurrentPlanAddons(availableAddons);
        }catch(error){
            setCurrentPlanAddons([]);
            setCurrentPlanAddonsError(getAddonApiErrorMessage(error, 'Unable to load add-ons for your current plan.'));
        }finally{
            if(mountedRef.current){
                setCurrentPlanAddonsLoading(false);
            }
        }
    }, [canManagePlanAddons, currentSubscriptionPlanId]);

    useEffect(() => {
        if(!currentSubscriptionPlanId){
            setLocallyActivatedAddonPriceIds([]);
            return;
        }
        setLocallyActivatedAddonPriceIds([]);
    }, [currentSubscriptionPlanId]);

    useEffect(() => {
        if(selectedTab !== 'settings'){
            return;
        }
        loadCurrentPlanAddons();
    }, [loadCurrentPlanAddons, selectedTab]);

    useEffect(() => {
        const shouldLoadAccountAccessData = ['settings', 'licenses', 'subscription'].includes(selectedTab);
        if(!shouldLoadAccountAccessData || !user){
            setOwnedBundles([]);
            setOwnedBundlesLoading(false);
            setOwnedBundlesError(null);
            setPurchasedAssets([]);
            setPurchasedAssetsLoading(false);
            setPurchasedAssetsError(null);
            setAssetOrders([]);
            setAssetOrdersLoading(false);
            setAssetOrdersError(null);
            return;
        }

        let isCancelled = false;
        setOwnedBundlesLoading(true);
        setOwnedBundlesError(null);
        setPurchasedAssetsLoading(true);
        setPurchasedAssetsError(null);
        setAssetOrdersLoading(true);
        setAssetOrdersError(null);

        Promise.allSettled([
            getPurchasedBundlesFn(),
            getBundlesFn(),
            getMarketplacePurchasedAssetsFn(),
            getMarketplaceOrdersFn()
        ])
            .then(([purchasedResult, bundlesResult, purchasedAssetsResult, assetOrdersResult]) => {
                if(isCancelled){
                    return;
                }

                const purchasedEntries = purchasedResult.status === 'fulfilled' && Array.isArray(purchasedResult.value)
                    ? purchasedResult.value
                    : [];
                const activeBundles = bundlesResult.status === 'fulfilled' && Array.isArray(bundlesResult.value)
                    ? bundlesResult.value
                    : [];
                if(purchasedResult.status !== 'fulfilled' || bundlesResult.status !== 'fulfilled'){
                    console.log("error while loading owned bundles", purchasedResult.status !== 'fulfilled' ? purchasedResult.reason : bundlesResult.reason);
                    setOwnedBundlesError('Unable to load bundle access right now.');
                }
                setOwnedBundles(resolveOwnedBundles({
                    purchasedEntries,
                    activeBundles
                }));

                if(purchasedAssetsResult.status === 'fulfilled'){
                    setPurchasedAssets(Array.isArray(purchasedAssetsResult.value) ? purchasedAssetsResult.value : []);
                }else{
                    console.log("error while loading purchased assets", purchasedAssetsResult.reason);
                    setPurchasedAssets([]);
                    setPurchasedAssetsError('Unable to load purchased assets right now.');
                }

                if(assetOrdersResult.status === 'fulfilled'){
                    const normalizedOrders = Array.isArray(assetOrdersResult.value) ? assetOrdersResult.value : [];
                    setAssetOrders(normalizedOrders.filter((order) => hasMarketplaceAssetOrderItems(order)));
                }else{
                    console.log("error while loading asset orders", assetOrdersResult.reason);
                    setAssetOrders([]);
                    setAssetOrdersError('Unable to load asset purchases right now.');
                }
            })
            .finally(() => {
                if(isCancelled){
                    return;
                }
                setOwnedBundlesLoading(false);
                setPurchasedAssetsLoading(false);
                setAssetOrdersLoading(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [selectedTab, user, bundleCheckoutStatus]);

    const handleSubscribeAddon = async (addonPriceId) => {
        const parsedAddonPriceId = Number(addonPriceId);
        if(!Number.isFinite(parsedAddonPriceId) || !canManagePlanAddons){
            return;
        }
        if(activeAddonPriceIdsSet.has(parsedAddonPriceId)){
            return;
        }

        setCurrentPlanAddonsError(null);
        setSubscribingAddonPriceIds((prev) => ({
            ...prev,
            [parsedAddonPriceId]: true
        }));

        try{
            await addSubscriptionAddonFn(parsedAddonPriceId);
            setLocallyActivatedAddonPriceIds((prev) => (
                prev.includes(parsedAddonPriceId) ? prev : [...prev, parsedAddonPriceId]
            ));
            reloadCurrentUser();
        }catch(error){
            setCurrentPlanAddonsError(getAddonApiErrorMessage(error, 'Unable to activate this add-on.'));
        }finally{
            if(mountedRef.current){
                setSubscribingAddonPriceIds((prev) => {
                    const next = {...prev};
                    delete next[parsedAddonPriceId];
                    return next;
                });
            }
        }
    };

    const teamSeatContextsData = visibleTeamSeatContexts.map((context) => {
        const contextPayload = normalizeSeatContextPayload(context.key, teamSeatsData?.[context.key]);
        const seatsTotal = parseSeatMetric(contextPayload.seats_total);
        const seatsUsed = parseSeatMetric(contextPayload.seats_used);
        const seatsAvailable = parseSeatMetric(contextPayload.seats_available);
        const seatsUsagePercent = seatsTotal > 0 ? Math.min(100, Math.round((seatsUsed / seatsTotal) * 100)) : 0;

        return {
            ...context,
            payload: contextPayload,
            seatsTotal,
            seatsUsed,
            seatsAvailable,
            seatsUsagePercent,
            isDisabled: seatsTotal === 0
        };
    });

    const hasPremiumPluginsPolicy = hasPremiumPluginsAccess(user?.team?.subscription?.plan);
    const teamMemberLimit = getTeamMemberLimit(user, teamBundleMemberCapacity);
    const showTeamTab = canShowTeamTab(user, teamBundleMemberCapacity);
    const accessiblePluginLicenses = getAccessiblePluginLicenses(teamAppLicenses, user);
    const showLicensesTab = Boolean(user?.is_team_owner || user?.team || teamAppLicenses.length > 0 || purchasedAssets.length > 0);
    const showSeatsSection = user?.is_team_owner && visibleTeamSeatContexts.length > 0 && (teamSeatsLoading || !!teamSeatsError || !!teamSeatsData);
    const teamMembersUsed = teamMembers.length;
    const canUseTeamInvitations = canInviteTeamMembers(user, teamBundleMemberCapacity);
    const teamMembersTotal = teamMemberLimit > 0 ? teamMemberLimit : teamMembersUsed;
    const teamSeatsContextsLabel = visibleTeamSeatContexts
        .map((context) => context.key === 'desktop' ? 'Portal' : context.label)
        .join('/');
    const teamSeatsSectionTitle = `${teamSeatsContextsLabel} simultaneous session seats usage`;
    const currentSeatIdFromToken = useMemo(() => {
        try{
            const token = getCookie(configValues.sessionTokenCookieKey);
            if(!token){
                return null;
            }
            const tokenParts = token.split('.');
            if(tokenParts.length < 2){
                return null;
            }
            const payloadBase64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
            const paddedPayload = payloadBase64.padEnd(Math.ceil(payloadBase64.length / 4) * 4, '=');
            const payload = JSON.parse(window.atob(paddedPayload));
            const seatIdFromPayload =
                payload?.seat_id ??
                payload?.seatId ??
                payload?.session_seat_id ??
                payload?.sessionSeatId ??
                payload?.sid ??
                payload?.session_id ??
                payload?.sessionId ??
                null;

            if(seatIdFromPayload === null || seatIdFromPayload === undefined){
                return null;
            }
            return String(seatIdFromPayload);
        }catch(error){
            return null;
        }
    }, []);

    const getUserDisplayName = (firstName, lastName, email, fallbackId) => {
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        if(fullName){
            return fullName;
        }
        if(email){
            return email;
        }
        return fallbackId ? `User #${fallbackId}` : 'Unknown user';
    };

    const formatSeatDateTime = (value) => {
        if(!value){
            return 'N/A';
        }
        const date = new Date(value);
        if(Number.isNaN(date.getTime())){
            return 'N/A';
        }
        return date.toLocaleString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSeatTimestampMs = (value) => {
        if(!value){
            return null;
        }
        const timestamp = new Date(value).getTime();
        if(Number.isNaN(timestamp)){
            return null;
        }
        return timestamp;
    };

    const formatSeatCountdown = (remainingMs) => {
        if(!Number.isFinite(remainingMs) || remainingMs <= 0){
            return '00:00:00';
        }

        const totalSeconds = Math.floor(remainingMs / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const hh = String(hours).padStart(2, '0');
        const mm = String(minutes).padStart(2, '0');
        const ss = String(seconds).padStart(2, '0');
        if(days > 0){
            return `${days}d ${hh}:${mm}:${ss}`;
        }
        return `${hh}:${mm}:${ss}`;
    };

    const getSeatRemainingMs = (expiresAt) => {
        const expiresMs = getSeatTimestampMs(expiresAt);
        if(!expiresMs){
            return 0;
        }
        return Math.max(0, expiresMs - seatTickerMs);
    };

    const getSeatCountdownPercent = (seat) => {
        const expiresMs = getSeatTimestampMs(seat?.expires_at);
        if(!expiresMs){
            return 0;
        }

        const remainingMs = Math.max(0, expiresMs - seatTickerMs);
        const lastUsedMs = getSeatTimestampMs(seat?.last_used_at);
        if(lastUsedMs && lastUsedMs < expiresMs){
            const totalMs = expiresMs - lastUsedMs;
            if(totalMs <= 0){
                return 0;
            }
            return Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));
        }

        return remainingMs > 0 ? 100 : 0;
    };

    const getSeatStateKey = (seat) => {
        if(!seat?.seat_id){
            return null;
        }
        const seatContext = seat?.seat_context || 'unknown';
        return `${seatContext}:${seat.seat_id}`;
    };

    const getIsCurrentSeat = (seat) => {
        if(!seat?.seat_id){
            return false;
        }

        if(currentSeatIdFromToken){
            return String(seat.seat_id) === currentSeatIdFromToken;
        }

        return false;
    };

    const handleRevokeSeat = async (seat) => {
        if(!seat?.seat_id || !canRevokeSeats || !user?.is_team_owner){
            return;
        }

        const seatStateKey = getSeatStateKey(seat);
        if(!seatStateKey){
            return;
        }

        if(getIsCurrentSeat(seat)){
            showTeamSeatsToast('This is your current seat session. Revoke it from another active owner session.');
            return;
        }

        const seatUserLabel = getUserDisplayName(seat?.user?.first_name, seat?.user?.last_name, seat?.user?.email, seat?.user?.id);
        const seatDeviceLabel = getSeatDeviceLabel(seat, seat?.seat_context);
        const hasConfirmed = window.confirm(`Are you sure you want to revoke ${seatDeviceLabel} for ${seatUserLabel}?`);
        if(!hasConfirmed){
            return;
        }

        setRevokingSeatIds(prev => ({
            ...prev,
            [seatStateKey]: true
        }));

        try{
            const response = await teamFn.revokeSeat(seat.seat_id);
            showTeamSeatsToast(response?.message || 'Seat revoked successfully');
            fetchTeamSeats();
        }catch(error){
            const statusCode = error?.response?.status;
            if(statusCode === 403){
                setCanRevokeSeats(false);
                showTeamSeatsToast('You no longer have permission to revoke seats');
            }else if(statusCode === 404){
                showTeamSeatsToast('Seat no longer active');
                fetchTeamSeats();
            }else{
                const message = error?.response?.data?.message || error?.message || 'Unable to revoke seat';
                showTeamSeatsToast(message);
            }
        }finally{
            if(!mountedRef.current){
                return;
            }
            setRevokingSeatIds(prev => {
                const next = {...prev};
                delete next[seatStateKey];
                return next;
            });
        }
    };

    const dismissBundleCheckoutMessage = useCallback(() => {
        const nextParams = new URLSearchParams(searchParams.toString());
        nextParams.delete('bundle_checkout');
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const renderBillingSection = () => (
        <div className="invoicing row-section">
            <div className="title m-b-8">
                Invoicing details
            </div>
            <div className="invoicing-data-form">
                <Input
                    label="Billing name:"
                    input={true}
                    onChange={(value) => handleBillingChange({'business_name': value})}
                    ref={billingDataRef['business_name']}
                />
                <Input
                    label="VAT number:"
                    input={true}
                    onChange={(value) => handleBillingChange({'vat': value})}
                    ref={billingDataRef['vat']}
                />
                <Input
                    label="Billing Address:"
                    input={true}
                    onChange={(value) => handleBillingChange({'address': value})}
                    ref={billingDataRef['address']}
                />
                <Input
                    label="Additional details:"
                    input={true}
                    onChange={(value) => handleBillingChange({'invoice_notes': value})}
                    ref={billingDataRef['invoice_notes']}
                />
                <div className="btn-row">
                    <a className="submit-form" onClick={handleSaveBillingData}>
                        Save Billing Information
                    </a>
                </div>
            </div>
            <div className="title m-b-8 m-t-32">
                Invoices History:
            </div>
            {invoices && Array.isArray(invoices) && invoices.length === 0 &&
            <div className="note m-t-12">No invoices available yet.</div>
            }
            {invoices && Array.isArray(invoices) && invoices.length > 0 &&
            <>
                <div className='note m-t-12'>Download Past period invoices:</div>
                <ul className="feat-list">
                    {invoices.map((invoice, index) =>
                        <li key={`invoice-${index}`}>
                            <a href={invoice.url} target="_blank" rel="noreferrer">
                            Total: ${invoice.total} Issued on {invoice.issue_at} <span className="glyphicon glyphicon-cloud-download" ></span>
                            </a>
                        </li>
                    )}
                </ul>
            </>
            }
        </div>
    );

    const renderAccountAccessSummary = () => {
        const isTeamMember = !user?.is_team_owner && !!user?.team;
        const subscription = user?.team?.subscription;
        const plan = subscription?.plan;
        const productName = plan?.product?.name;
        const isPaymentPastDue = isStripePaymentPastDue(user);
        const teamName = getTeamDisplayName(user);
        const myAssetsHref = getProductioncrateSiteHref({environment: getEnvironment(), pathname: '/my-assets'});
        return (
            <div className="account-access-summary row-section">
                <div className="user">
                    <div className="pic m-b-16">
                        <img alt="Profile" src={`https://www.gravatar.com/avatar/${md5((user?.email || '').trim().toLowerCase())}?s=200&d=${encodeURIComponent('https://upload.wikimedia.org/wikipedia/commons/a/ac/Default_pfp.jpg')}`} />
                    </div>
                    <div className="name">{getUserIdentityLabel(user)}</div>
                </div>
                <div className="user-level">
                    <Membership />
                    {isTeamMember &&
                    <div className="claim">You are a member of {teamName} team.</div>
                    }
                    {!premiumAssetsPolicy &&
                    <div className="claim">You can download {planDownloadsPerDay} effects per day from the free archives.</div>
                    }
                    {premiumAssetsPolicy &&
                    <div className="claim">
                        {['ENTERPRISE', 'EDUCATORS'].includes(productName) ? `Your team can download ${planDownloadsPerDay} assets per day.` : `You can download ${planDownloadsPerDay} assets per day.`}
                    </div>
                    }
                    <ul className="feat-list">
                        {hasPremiumPluginsPolicy && <li>You have unrestricted access to Plugins without watermark.</li>}
                        {!premiumAssetsPolicy && <li>About a third of our content is free</li>}
                        {!premiumAssetsPolicy && <li>Items marked with a star (<span className="glyphicon glyphicon-star star"></span>) are exclusively PRO.</li>}
                        {premiumAssetsPolicy && <li>You have total access to all the best quality items from the ProductionCrate archives.</li>}
                        {premiumAssetsPolicy && <>
                            <li><a href="https://www.productioncrate.com/vfx">Browse thousands of VFX &amp; media elements from the FootageCrate archives.</a></li>
                            <li><a href="https://www.productioncrate.com/sfx">Browse thousands SFX &amp; music assets from the SoundsCrate archives.</a></li>
                            <li><a href="https://www.productioncrate.com/graphics">Browse Ultra-HD production ready images and textures from the GraphicsCrate archives.</a></li>
                            <li><a href="https://www.productioncrate.com/3d">Browse photorealistic objects from the RenderCrate archives.</a></li>
                            <li><a href="https://contests.productioncrate.com/">Compete in the monthly 10 second video contest to win prizes and glory.</a></li>
                            <li><a href="https://learn.productioncrate.com/tutorials.html">Improve your production quality by learning with our tutorials.</a></li>
                        </>}
                        <li>You have {assetsAvailable} downloads remaining today.</li>
                        {purchasedAssets.length > 0 && <li>
                            <a href={myAssetsHref}>You have {purchasedAssets.length} asset entitlements.</a>
                        </li>}
                        {accessiblePluginLicenses.length > 0 && <li>
                            <a href={ACCOUNT_SECTION_PATHS.licenses} onClick={(event) => { event.preventDefault(); handleTabSelect('licenses'); }}>
                                You have {accessiblePluginLicenses.length} plugin licenses.
                            </a>
                        </li>}
                        {user?.is_team_owner && subscription && Number(plan?.price) > 1 &&
                        <li>{`Plan's settings: $${plan.price} per ${plan.periodicity_type} since ${subscription.started_at ? getReadableDate(subscription.started_at) : 'processing...'}.`}</li>
                        }
                    </ul>
                    {!isTeamMember && !premiumAssetsPolicy && !isPaymentPastDue &&
                    <div className="upgrade">
                        <div className="label">Upgrade:</div>
                        <div className="btn" onClick={() => handleUpgradeClick('register')}>Credit / Debit Cards</div>
                        <div className="accent m-t-18">Upgrade for Full Access to our Best Quality Content.</div>
                        <div className="note">Access to all our libraries, Downloads up to 4K, Thousands more items to choose from, 200+ daily downloads.</div>
                    </div>
                    }
                    {!isTeamMember && <div className="claim">Enjoy, and make it awesome!</div>}
                </div>
            </div>
        );
    };

    return (
        <div className="layout">
            <Header ref={headerRef} />
            {!user &&
                <div className="empty-content">
                    <Spinner />
                </div>
            }
            {user && 
            <div className="account m-h-a">
                <Modal
                    visible={cancelConfirmationState !== 'idle'}
                    title={cancelConfirmationState === 'checking'
                        ? 'Confirming your cancellation...'
                        : "We couldn't confirm your cancellation yet"}
                    msg={cancelConfirmationState === 'checking'
                        ? 'We are checking your subscription status with Stripe. This can take a few seconds.'
                        : 'Please try the status check again. Your subscription will not be canceled again.'}
                    cta={cancelConfirmationState === 'error' ? 'Retry status check' : false}
                    ctaCallback={handleRetryCancellationConfirmation}
                    style={{height: 'max-content'}}
                />
                <Modal
                    visible={cancelSuccessModal}
                    title="Subscription Cancelled!"
                    msg={user?.team?.subscription?.expired_at
                        ? `Access remains active until ${getReadableDate(user.team.subscription.expired_at)}.`
                        : undefined}
                    cta="Close"
                    ctaCallback={() => setCancelSuccessModal(false)}
                    style={{height: '200px'}}
                />
                <Modal
                    visible={pauseSuccessModal}
                    title="Subscription Paused!"
                    cta="Close"
                    ctaCallback={() => setPauseSuccessModal(false)}
                    style={{height: '200px'}}
                />
                <Modal
                    visible={resumeSuccessModal}
                    title="Subscription Resumed!"
                    cta="Close"
                    ctaCallback={() => setResumeSuccessModal(false)}
                    style={{height: '200px'}}
                />
                <Modal
                    visible={pauseConfigModal}
                    title="Pause Subscription"
                    msg={
                        <div style={{fontSize:'16px', lineHeight: '24px'}}>
                            <label style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px'}}>
                                <input type="radio" name="pauseMode" checked={pauseMode==='indefinite'} onChange={() => setPauseMode('indefinite')} />
                                Pause indefinitely (until you resume)
                            </label>
                            <label style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px'}}>
                                <input type="radio" name="pauseMode" checked={pauseMode==='days'} onChange={() => setPauseMode('days')} />
                                Pause for
                                <input
                                    type="number"
                                    min="1"
                                    value={pauseResumesInDays}
                                    onChange={(e) => setPauseResumesInDays(e.target.value)}
                                    disabled={pauseMode !== 'days'}
                                    style={{width: '90px'}}
                                />
                                days
                            </label>
                            <label style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px'}}>
                                <input type="radio" name="pauseMode" checked={pauseMode==='date'} onChange={() => setPauseMode('date')} />
                                Pause until
                                <input
                                    type="datetime-local"
                                    value={pauseResumesAt}
                                    onChange={(e) => setPauseResumesAt(e.target.value)}
                                    disabled={pauseMode !== 'date'}
                                    style={{width: '220px'}}
                                />
                            </label>
                            {pauseError &&
                                <div style={{color:'#ff4444', marginTop: '14px'}}>
                                    {pauseError}
                                </div>
                            }
                        </div>
                    }
                    className="danger"
                    showCloseIcon={true}
                    cta="Confirm Pause"
                    ctaCallback={() => handlePauseSubscription()}
                    ctaClose="Back"
                    ctaCloseCallback={() => setPauseConfigModal(false)}
                    overlayCallback={() => setPauseConfigModal(false)}
                    style={{height: 'max-content'}}
                />
                <Modal
                    visible={passwordChangeSuccessModal}
                    title="Your password has been updated successfully!"
                    cta="Close"
                    ctaCallback={() => setPasswordChangeSuccessModal(false)}
                    style={{height: '200px'}}
                />
                {/* Commented for the moment ... */}
                <div className={`tab-list-mobile-trigger ${mobileVisible?"mobileVisible":""}`} onClick={() => setMobileVisible(!mobileVisible)}>
                    <span className={`glyphicon ${mobileVisible?"glyphicon-cross":"glyphicon-settings"}`}></span>
                </div>
                <div className={`tab-list ${mobileVisible?"mobileVisible":""}`}>
                    <div className={`tab ${selectedTab=='settings'?'active':''}`} onClick={() => handleTabSelect('settings')}>Account Home</div>
                    {showTeamTab && 
                    <div className={`tab ${selectedTab=='team'?'active':''}`} onClick={() => handleTabSelect('team')}>
                        Teams &amp; Seats
                    </div>
                    }
                    {user?.is_team_owner && <div className={`tab ${selectedTab=='subscription'?'active':''}`} onClick={() => handleTabSelect('subscription')}>
                        Billing &amp; Purchases
                    </div>
                    }
                    {showLicensesTab &&
                    <div className={`tab ${selectedTab=='licenses'?'active':''}`} onClick={() => handleTabSelect('licenses')}>
                        Licenses &amp; Entitlements
                    </div>
                    }
                    <a
                        className="tab"
                        href="https://discord.com/invite/46dJfD5jgV"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{borderBottom: 'help'}}
                    >
                        Help & Support
                    </a>
                    <a target='_blank' className='m-t-a m-h-a sticky_cta' href='mailto:support@productioncrate.com' referrerPolicy='no-referrer' rel="nofollow">
                    <button class={`cta btn `} >
                        CONTACT SUPPORT
                    </button>
                    </a>
                </div>
                {loadingGeneral && 
                    <div className="general-loading">
                        <Spinner label="Please wait..." />
                    </div>
                }
                <div className="account-content">
                    {showBundleCheckoutMessage &&
                    <div className={`bundle-checkout-feedback ${bundleCheckoutStatus === 'success' ? 'is-success' : 'is-cancelled'}`}>
                        <div className="bundle-checkout-feedback__message">
                            {bundleCheckoutStatus === 'success'
                                ? 'Bundle checkout completed successfully. Your purchased bundle will appear as OWNED on the plans page.'
                                : 'Bundle checkout was cancelled. You can return to the plans page and try again whenever you are ready.'}
                        </div>
                        <div className="bundle-checkout-feedback__actions">
                            <a href="/register" className="bundle-checkout-feedback__link">Go to Plans</a>
                            <button type="button" className="bundle-checkout-feedback__dismiss" onClick={dismissBundleCheckoutMessage}>
                                Dismiss
                            </button>
                        </div>
                    </div>
                    }
                    {selectedTab==='consumptions' && 
                    <>
                        <Consumptions />
                    </>
                    }
                    {selectedTab==='settings' && 
                    <>
                        {renderAccountAccessSummary()}
                        <AccountLocationBanners />
                        <FormatSelectionWithVideo settings={user.settings} settingsCallback={console.log} />
                        {shouldRenderPlanAddonsSection &&
                        <div className="plan-addons row-section">
                            <div className="title m-b-8">Plan Add-ons</div>
                            <div className="claim">
                                Add optional features to your current subscription without changing your main plan.
                            </div>
                            {currentPlanAddonsError &&
                            <div className="error">{currentPlanAddonsError}</div>
                            }
                            {currentPlanAddonsLoading &&
                            <div className="note">Loading available add-ons...</div>
                            }
                            {!currentPlanAddonsLoading && currentPlanAddons.length > 0 &&
                            <div className="addons-grid">
                                {currentPlanAddons.map((addon) => {
                                    const addonId = Number(addon.id);
                                    const addonPriceId = getAddonPriceId(addon);
                                    const addonPeriodicityType = getAddonPeriodicityType(addon);
                                    const isValidAddonId = Number.isFinite(addonId);
                                    const isValidAddonPriceId = Number.isFinite(addonPriceId);
                                    const isSubscribed = (
                                        (isValidAddonId && activeAddonIdsSet.has(addonId)) ||
                                        (isValidAddonPriceId && activeAddonPriceIdsSet.has(addonPriceId))
                                    );
                                    const isSubscribing = isValidAddonPriceId && subscribingAddonPriceIds[addonPriceId] === true;
                                    return (
                                        <div className="addon-card" key={`current-plan-addon-${addon.id}`}>
                                            <div className="addon-card__header">
                                                <div className="addon-card__name">{addon.name}</div>
                                                <div className="addon-card__price">{`${formatAddonPrice(getAddonPriceValue(addon))}${addonPeriodicityType ? ` / ${addonPeriodicityType}` : ''}`}</div>
                                            </div>
                                            <div className="addon-card__description">{addon.description || 'No description available.'}</div>
                                            <button
                                                type="button"
                                                className={`btn addon-card__cta ${isSubscribed ? 'addon-card__cta--subscribed' : ''}`}
                                                disabled={!isValidAddonPriceId || isSubscribed || isSubscribing}
                                                onClick={() => handleSubscribeAddon(addonPriceId)}
                                            >
                                                {isSubscribed ? 'Subscribed' : (isSubscribing ? 'Adding...' : 'Add to subscription')}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            }
                        </div>
                        }
                        <div className="referral-program row-section">
                            <div className="title" >
                                <SlideToggle 
                                    initial={footagecrateBlender}
                                    label={`FootageCrate Blender Addon`}
                                    onToggle={async (value) => {
                                        if(value){
                                            await handleEnableFootagecrateBlender();
                                        }else{
                                            await handleDisableFootagecrateBlender();
                                        }
                                    }}
                                />
                            </div>
                            <div className="title" >
                                <SlideToggle 
                                    initial={newsletter}
                                    label="Subscribe to Newsletter"
                                    onToggle={async (value) => {
                                        if(value){
                                            await handleSubscribeNewsletter(false);
                                        }else{
                                            await handleUnsubscribeNewsletter(false);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        
                        <div className="additional-data row-section">
                            <div className="title m-b-8">
                                Personal Information
                            </div>
                            <div className="additional-data-form">
                            <Input
                                label="First name:"
                                input={true}
                                persistedValue={getPersonalInformationValue(user, 'first_name')}
                                onSave={(value) => handleProfileData('first_name', value)}
                                inputProps={PROFILE_INPUT_PROPS.first_name}
                                ref={personalDataRef['first_name']}
                            />
                            {profileDataErrors.first_name &&
                            <div className="error profile-field-error">{profileDataErrors.first_name}</div>
                            }
                            <Input
                                label="Last name:"
                                input={true}
                                persistedValue={getPersonalInformationValue(user, 'last_name')}
                                onSave={(value) => handleProfileData('last_name', value)}
                                inputProps={PROFILE_INPUT_PROPS.last_name}
                                ref={personalDataRef['last_name']}
                            />
                            {profileDataErrors.last_name &&
                            <div className="error profile-field-error">{profileDataErrors.last_name}</div>
                            }
                            <Input
                                label="Username:"
                                input={true}
                                loadingMessage="Checking availability..."
                                persistedValue={getPersonalInformationValue(user, 'username')}
                                onSave={(value) => handleProfileData('username', value)}
                                inputProps={PROFILE_INPUT_PROPS.username}
                                ref={personalDataRef['username']}
                            />
                            {profileDataErrors.username &&
                            <div className="error profile-field-error">{profileDataErrors.username}</div>
                            }
                            <Input  
                                label="Profession:"
                                persistedValue={getPersonalInformationValue(user, 'profession')}
                                options={[
                                    { label: '', value: null },
                                    { label: 'Vlogger', value: 'vlogger' },
                                    { label: 'Business owner', value: 'business-owner' },
                                    { label: 'VFX artist', value: 'vfx-artist' },
                                    { label: 'Motion graphics artist', value: 'motion-graphics-artist' },
                                    { label: 'Teacher', value: 'teacher' },
                                    { label: 'Student', value: 'student' },
                                    { label: 'Video editor', value: 'video-editor' },
                                    { label: 'Filmmaker', value: 'filmmaker' },
                                    { label: 'Developer', value: 'developer' },
                                    { label: 'Other', value: 'other' }
                                ]}
                                onSave={(value) => handlePersonalData({'profession': value})}
                                ref={personalDataRef['profession']}
                            />
                            <Input  
                                label="Software Preferences:"
                                persistedValue={getPersonalInformationValue(user, 'software_preferences')}
                                options={[
                                    { label: '', value: null },
                                    { label: "After Effects", value: "after-effects" },
                                    { label: "iMovie", value: "imovie" },
                                    { label: "Adobe Premiere", value: "adobe-premiere" },
                                    { label: "DaVinci Resolve", value: "davinci-resolve" },
                                    { label: "Final Cut", value: "final-cut" },
                                    { label: "Wondershare Filmora", value: "wondershare-filmora" },
                                    { label: "Sony Vegas", value: "sony-vegas" },
                                    { label: "BlackMagic Fusion", value: "blackmagic-fusion" },
                                    { label: "Kinemaster", value: "kinemaster" },
                                    { label: "Nuke", value: "nuke" },
                                    { label: "Blender", value: "blender" },
                                    { label: "Power Director", value: "power-director" },
                                    { label: "Photoshop", value: "photoshop" },
                                    { label: "CapCut", value: "capcut" },
                                    { label: "Cinema 4D", value: "cinema-4d" },
                                    { label: "Houdini", value: "houdini" },
                                    { label: "Maya", value: "maya" },
                                    { label: "Unity", value: "unity" },
                                    { label: "Unreal Engine", value: "unreal-engine" },
                                    { label: "Other", value: "other" }
                                ]}
                                onSave={(value) => handlePersonalData({'software_preferences': value})}
                                ref={personalDataRef['software_preferences']}
                            />
                            <Input  
                                label="Usage:"
                                persistedValue={getPersonalInformationValue(user, 'usage_purpose')}
                                options={[
                                    { label: '', value: null },
                                    { label: "Not Sure", value: "not-sure" },
                                    { label: "School Project", value: "school-project" },
                                    { label: "Films", value: "feature-film" },
                                    { label: "Commercials", value: "client-projects-commercials" },
                                    { label: "YouTube", value: "youtube" },
                                    { label: "Just exploring!", value: "just-browsing" },
                                    { label: "Games", value: "games" },
                                    { label: "Instagram", value: "instagram" },
                                    { label: "TikTok", value: "tiktok" },
                                    { label: "Music Videos", value: "music-videos" },
                                    { label: "Graphics", value: "graphic-design" },
                                    { label: "VFX Shots", value: "vfx-shots" },
                                    { label: "Other Socials", value: "other-socials" },
                                    { label: "Learning/Practice", value: "learning-practice" },
                                    { label: "Other", value: "other" }
                                ]}
                                onSave={(value) => handlePersonalData({'usage_purpose': value})}
                                ref={personalDataRef['usage_purpose']}
                            />
                            <Input  
                                label="Website:"
                                input={true}
                                persistedValue={getPersonalInformationValue(user, 'website_channel')}
                                onSave={(value) => handlePersonalData({'website_channel': value})}
                                ref={personalDataRef['website_channel']}
                            />
                            <Input  
                                label="How did you found us?"
                                persistedValue={getPersonalInformationValue(user, 'how_hear')}
                                options={[
                                    { label: '', value: null },
                                    { label: 'Instagram', value: 'Instagram' },
                                    { label: 'TikTok', value: 'TikTok' },
                                    { label: 'ChatGPT', value: 'ChatGPT' },
                                    { label: 'Youtube', value: 'Youtube' },
                                    { label: 'Google Search', value: 'Google Search' },
                                    { label: 'Feature Film', value: 'Feature Film' },
                                    { label: 'Friend', value: 'Friend' },
                                    { label: 'Advertisement', value: 'Advertisement' },
                                    { label: 'Other', value: 'other' },
                                ]
                                }
                                onSave={(value) => handlePersonalData({'how_hear': value})}
                                ref={personalDataRef['how_hear']}
                            />
                            {false &&
                            <Input  
                                label="What do you want us to make next?:"
                                input={true}
                                onSave={(value) => handlePersonalData({'next_desire': value})}
                                ref={personalDataRef['next_desire']}
                            />
                            }
                            </div>
                        </div>
                        <div className="change-password row-section">
                            <div className="title m-b-8">
                                Change password
                            </div>
                            <form onSubmit={event => {event.preventDefault();handleNewPasswordSave();}} >
                                <div className="additional-data-form" style={{minHeight: '152px'}}>
                                    <input type="submit" style={{display: "none"}} />
                                    <Input  
                                        label="New Password"
                                        input={true}
                                        onChange={(value) => handleChangeNewPassword(value)}
                                        type={"password"}
                                        ref={newPasswordRef}
                                    />
                                    <Input  
                                        label="Repeat:"
                                        input={true}
                                        onChange={(value) => handleChangeRepeatPassword(value)}
                                        type={"password"}
                                        ref={repeatPasswordRef}
                                    />
                                    <button className="submit-form" onClick={()=>handleNewPasswordSave()}>Save password</button>
                                    {passwordError && Array.isArray(passwordError) &&
                                        <div className="error">
                                            {passwordError.map((error, k) => <div key={`error-key-${k}`}>{error}</div>)}
                                        </div>
                                    }
                                    {passwordError && !Array.isArray(passwordError) &&
                                        <div className="error">
                                            {passwordError}
                                        </div>
                                    }
                                </div>
                            </form>
                        </div>
                        {false &&
                        <div className="referral-program row-section">
                            <div className="title m-b-8">
                            Earn Revenue with ProductionCrate!
                            </div>
                            <ul className='feat-list'>
                                <li>
                                Get paid 15% commission from all sales referrals, including future recurring sales!
                                </li>
                                <li>
                                Payouts are processed automatically.
                                </li>
                                <li>
                                Links can be shared in credits, descriptions, or anywhere you see fit.
                                </li>
                            </ul>
                            <div className="btn-row">
                                <a className="submit-form" target="_blank" rel="nofollow" href="https://productioncrate.partnerstack.com/">
                                    Join Affilliate Program
                                </a>
                            </div>
                        </div>
                        }
                    </>
                    }
                    {selectedTab==='settings' &&
                    <>
                    <div className="news-updates row-section">
                        <div className="title m-b-8">
                            News & Updates:
                        </div>
                        <div className="claim">
                            <a href="https://www.productioncrate.com/search/new">Bookmark us</a> & <a href="https://www.youtube.com/channel/UCtl5VdRFeerk0-xAGBpMbdw?sub_confirmation=1" target="_blank" rel="noreferrer">Subscribe</a> to find out about new ProductionCrate content first!
                        </div>
                        <ul className='feat-list'>
                            <li>
                                <a href="https://www.productioncrate.com/search/new">
                                View the latest 100 resources anytime by searching "new" (new items are bulk uploaded every few days).
                                </a>
                            </li>
                            <li>
                                <a href="https://www.youtube.com/productioncrate">
                                Catch our demos and promos by subscribing on YouTube (hit the bell for updates)
                                </a>
                            </li>
                            <li>
                                For more tips, teasers, and behind the scenes stuff - follow us on facebook, twitter, and instagram. Here is our facebook group page where you can post your video productions for feedback from us and the community.
                            </li>
                        </ul>
                        <div className="btn-row">
                            <a className="btn" href="https://www.instagram.com/productioncrate">
                                Follow on Instagram
                                <span className="glyphicon glyphicon-right"></span>    
                            </a>
                            <a className="btn" href="https://www.youtube.com/user/FootageCrate?sub_confirmation=1">
                                Subscribe on YouTube
                                <span className="glyphicon glyphicon-right"></span>
                            </a>
                            <a className="btn" href="https://www.productioncrate.com/search/new">
                                Latest 500 Effects
                                <span className="glyphicon glyphicon-right"></span>
                            </a>
                            <a className="btn" href="https://www.productioncrate.com/content-queue.html">
                                User Requests
                                <span className="glyphicon glyphicon-right"></span>
                            </a>
                        </div>
                    </div>
                    </>
                    }
                    {selectedTab==='settings' &&
                    <div className="invoicing row-section">
                        <div className="title m-b-8">
                            User Session
                        </div>
                        <button onClick={handleExpireAllSessions} className="btn cta" style={{cursor: 'pointer'}}>Logout from all devices</button>
                    </div>
                    }
                    {selectedTab==='subscription' && user?.is_team_owner &&
                    <>
                        {renderAccountAccessSummary()}
                        {user.team.subscription.plan.price > 0 && (user.team.subscription.state === 'active' || user.team.subscription.state === 'paused' || user.team.subscription.paused === true) &&
                        <div className={`row-section subscription-block ${subscriptionExpand?"active":""}`}>
                            <div className="title m-b-8" onClick={() => setSubscriptionExpand(!subscriptionExpand)}>
                                Subscription Management
                                <span className="glyphicon glyphicon-right menu-expand-icon" ></span>
                            </div>
                            <div className="hidden-content">
                                <div className='note m-b-8'>
                                    Note: some actions are irreversible, walk carefully.
                                </div>
                                {user.team.subscription?.paused === true &&
                                <div className='note m-b-8'>
                                    Subscription is paused {user.team.subscription.paused_until?`until ${getReadableDate(user.team.subscription.paused_until)}`:'indefinitely'}.
                                </div>
                                }
                                <div className="btn-row">
                                    {/* Pause is handled by Churnkey through Cancel Subscription for now.
                                    {user.team.subscription && user.team.subscription.paypal !== true && user.team.subscription.stripe === true && user.team.subscription?.paused !== true &&
                                        <div className="btn" onClick={handlePauseClick}>
                                            Pause Subscription
                                        </div>
                                    }
                                    */}
                                    {user.team.subscription.stripe === true && user.team.subscription.paypal !== true &&
                                        <button type="button" className="btn" onClick={handleUpdatePaymentMethod} disabled={openingPaymentMethod}>
                                            {openingPaymentMethod ? 'Opening Stripe...' : 'Update Payment Method'}
                                        </button>
                                    }
                                    {user.team.subscription.paypal === true &&
                                        <a className="btn" href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noopener noreferrer">
                                            Update Payment Method
                                        </a>
                                    }
                                    {user.team.subscription && user.team.subscription.paypal !== true && user.team.subscription.stripe === true && user.team.subscription?.paused === true &&
                                        <div className="btn" onClick={handleResumeSubscription}>
                                            Resume Subscription
                                        </div>
                                    }
                                    <div className="btn btn-danger" onClick={handleCancelClick}>
                                        Cancel Subscription
                                    </div>
                                    {false &&
                                    <div className="btn btn-danger" onClick={() => handleDeleteMe()}>
                                        Delete all my user data
                                    </div>
                                    }
                                </div>
                                {paymentMethodError && <div className="note m-t-12" role="alert">{paymentMethodError}</div>}
                            </div>
                            <Questionnaire user={user} visible={questionnaireModal} ctaCallback={() => setCancelConfirmModal(true)} expensiveCallback={questionnaireCallback} ctaCloseCallback={() => setQuestionnaireModal(false)} />
                            <Modal
                                visible ={cancelConfirmModal}
                                title="Subscription Cancelation"
                                msg={
                                    <div style={{fontSize:'16px', lineHeight: '26px'}}>
                                        {`Are you sure you want to cancel this subscription?`}
                                        <br />
                                        <b>
                                            {`${user.team.subscription.plan.product.name+' '+user.team.subscription.plan.name} ( $${user.team.subscription.plan.price} per ${user.team.subscription.plan.periodicity_type} )`}
                                        </b>
                                        <br />
                                        <div style={{color:'#ff4444'}}>
                                            {user.team.subscription.plan.cancellation_message?user.team.subscription.plan.cancellation_message:'You will lose any promotions previously applied, and this action cannot be reversed.'}
                                        </div>
                                    </div>
                                }
                                className="danger"
                                showCloseIcon={true}
                                cta="Confirm"
                                ctaCallback={() => handleCancelSubscription()}
                                ctaClose="Back"
                                ctaCloseCallback={() => setCancelConfirmModal(false)}
                                overlayCallback={() => setCancelConfirmModal(false)}
                                style={{height: 'max-content'}}
                            />
                            <Modal
                                visible ={applyPromoModal}
                                pretitle={`Account active since ${new Date(user.team.subscription.started_at).toLocaleDateString('en-US')}`}
                                title={<span style={{fontSize:'26px', lineHeight: '26px'}}><span style={{color: '#ff9f00'}}>{user.username !== ""?user.username:'You'}</span> {user.username !== ""?'qualifies':'qualify'} for a discount!</span>}
                                msg={
                                    <span style={{fontSize:'22px', lineHeight: '28px'}}>
                                        <b>PRO</b> access at <span style={{textDecoration: 'line-through'}}>$49</span> <b>$39</b> per Month
                                    </span>
                                }
                                showCloseIcon={false}
                                cta="Apply and Save 20%"
                                ctaCallback={applyPromoSubscribe}
                                ctaClose="Proceed with Cancelation"
                                ctaCloseCallback={() => handleCancelSubscription()}
                                overlayCallback={() => setApplyPromoModal(false)}
                                style={{height: 'max-content', borderWidth: '4px'}}
                                loadingSpinner={4000}
                                loadingLabel={"Checking account discount eligibility..."}
                            />
                        </div>
                        }
                        {renderBillingSection()}
                        <div className="bundle-ownership row-section">
                            <div className="title m-b-8">Bundle Access</div>
                            <div className="claim">
                                Bundles purchased or assigned to this account.
                            </div>
                            {ownedBundlesLoading &&
                            <div className="note">Loading bundle access...</div>
                            }
                            {!ownedBundlesLoading && ownedBundlesError &&
                            <div className="error">{ownedBundlesError}</div>
                            }
                            {!ownedBundlesLoading && !ownedBundlesError && ownedBundles.length === 0 &&
                            <div className="note">No bundles assigned yet.</div>
                            }
                            {!ownedBundlesLoading && !ownedBundlesError && ownedBundles.length > 0 &&
                            <div className="bundle-ownership__list">
                                {ownedBundles.map((bundle) => (
                                    <div className="bundle-ownership__item" key={`owned-bundle-${bundle.id ?? bundle.name}`}>
                                        <div className="bundle-ownership__item__name">{bundle.name}</div>
                                        <div className="bundle-ownership__item__meta">
                                            {bundle.accessLabel}
                                            {bundle.accessDateLabel ? ` on ${bundle.accessDateLabel}` : ''}
                                        </div>
                                        {Array.isArray(bundle.pluginNames) && bundle.pluginNames.length > 0 &&
                                        <div className="bundle-ownership__item__plugins">
                                            {bundle.pluginNames.join(', ')}
                                        </div>
                                        }
                                        {bundle.redirectUrl &&
                                        <a href={bundle.redirectUrl} target="_blank" rel="noopener noreferrer nofollow">
                                            Open purchase link
                                        </a>
                                        }
                                    </div>
                                ))}
                            </div>
                            }
                        </div>
                        <div className="marketplace-purchases row-section">
                            <div className="title m-b-8">Purchased Assets</div>
                            <div className="claim">
                                Asset entitlements currently owned by your team.
                            </div>
                            {purchasedAssetsLoading &&
                            <div className="note">Loading purchased assets...</div>
                            }
                            {!purchasedAssetsLoading && purchasedAssetsError &&
                            <div className="error">{purchasedAssetsError}</div>
                            }
                            {!purchasedAssetsLoading && !purchasedAssetsError && purchasedAssets.length === 0 &&
                            <div className="note">No purchased assets were found for this team yet.</div>
                            }
                            {!purchasedAssetsLoading && !purchasedAssetsError && purchasedAssets.length > 0 &&
                            <div className="marketplace-purchases__list">
                                {purchasedAssets.map((purchase) => (
                                    <div className="marketplace-purchases__item" key={`purchased-asset-${purchase.id}`}>
                                        <div className="marketplace-purchases__item__header">
                                            <div>
                                                <div className="marketplace-purchases__item__name">
                                                    <span className="marketplace-purchases__item__name-star" aria-hidden="true">
                                                        <span className="glyphicon glyphicon-star" />
                                                    </span>
                                                    <span>{purchase.asset_name}</span>
                                                </div>
                                                <div className="marketplace-purchases__item__meta">
                                                    Purchased {formatMarketplaceDateTime(purchase.purchased_at)}
                                                </div>
                                            </div>
                                            <div className="marketplace-purchases__item__price">
                                                {formatMarketplacePrice(purchase.amount_paid, 'usd')}
                                            </div>
                                        </div>
                                        <div className="marketplace-purchases__item__details">
                                            {purchase.provider &&
                                            <span>Provider: {purchase.provider}</span>
                                            }
                                            {purchase.provider_payment_id &&
                                            <span>Payment ID: {purchase.provider_payment_id}</span>
                                            }
                                            {purchase.asset_uuid &&
                                            <span>Asset UUID: {purchase.asset_uuid}</span>
                                            }
                                        </div>
                                        <div className="marketplace-purchases__item__actions">
                                            {purchase.invoice_link
                                                ? <a href={purchase.invoice_link} target="_blank" rel="noreferrer">Invoice</a>
                                                : <span className="muted">No invoice</span>
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>
                            }
                        </div>
                        <div className="marketplace-orders row-section">
                            <div className="title m-b-8">Asset Purchase History</div>
                            <div className="claim">
                                Orders from the marketplace that include asset purchases.
                            </div>
                            {assetOrdersLoading &&
                            <div className="note">Loading asset purchases...</div>
                            }
                            {!assetOrdersLoading && assetOrdersError &&
                            <div className="error">{assetOrdersError}</div>
                            }
                            {!assetOrdersLoading && !assetOrdersError && assetOrders.length === 0 &&
                            <div className="note">No marketplace asset purchases were found yet.</div>
                            }
                            {!assetOrdersLoading && !assetOrdersError && assetOrders.length > 0 &&
                            <div className="marketplace-orders__list">
                                {assetOrders.map((order) => {
                                    const assetItems = getMarketplaceOrderAssetItems(order);
                                    return (
                                        <div className="marketplace-orders__item" key={`asset-order-${order.id}`}>
                                            <div className="marketplace-orders__item__header">
                                                <div>
                                                    <div className="marketplace-orders__item__name">Order #{order.id}</div>
                                                    <div className="marketplace-orders__item__meta">
                                                        Paid {formatMarketplaceDateTime(order.paid_at || order.fulfilled_at, 'Pending')}
                                                    </div>
                                                </div>
                                                <div className="marketplace-orders__item__summary">
                                                    <span className={`status status-${String(order.status || '').toLowerCase()}`}>
                                                        {getMarketplaceOrderStatusLabel(order.status)}
                                                    </span>
                                                    <span>{formatMarketplacePrice(order.total_amount, order.currency)}</span>
                                                </div>
                                            </div>
                                            <div className="marketplace-orders__item__details">
                                                {order.provider &&
                                                <span>Provider: {order.provider}</span>
                                                }
                                                {order.provider_payment_id &&
                                                <span>Payment ID: {order.provider_payment_id}</span>
                                                }
                                                {order.provider_checkout_id &&
                                                <span>Checkout ID: {order.provider_checkout_id}</span>
                                                }
                                            </div>
                                            <div className="marketplace-orders__lines">
                                                {assetItems.map((item) => (
                                                    <div className="marketplace-orders__line" key={`asset-order-${order.id}-item-${item.id}`}>
                                                        <div>
                                                            <div className="marketplace-orders__line__name">
                                                                {item.asset_name || item.description || `Asset #${item.asset_id}`}
                                                            </div>
                                                            <div className="marketplace-orders__line__meta">
                                                                Qty {item.quantity}
                                                                {item.asset_uuid ? ` · UUID ${item.asset_uuid}` : ''}
                                                            </div>
                                                        </div>
                                                        <div className="marketplace-orders__line__price">
                                                            {formatMarketplacePrice(item.total_amount, order.currency)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            }
                        </div>
                    </>
                    }
                    {selectedTab==='licenses' &&
                    <>
                        {user?.is_team_owner &&
                        <TeamLicenseManager
                            members={teamMembers}
                            currentUser={user}
                            memberSlots={Math.max(0, teamMemberLimit - teamMembersUsed - teamInvites.length)}
                            invitationsLoading={teamInvitesLoading}
                            onInvitationSent={fetchTeamAndInvites}
                        />
                        }
                        {!user?.is_team_owner &&
                        <div className="account-licenses row-section">
                            <div className="title m-b-8">Plugin licenses</div>
                            {accessiblePluginLicenses.length === 0 &&
                            <div className="note">No plugin licenses are assigned to you yet.</div>
                            }
                            {accessiblePluginLicenses.length > 0 &&
                            <ul className="license-list">
                                {accessiblePluginLicenses.map((license, index) => (
                                    <li className="item" key={`account-plugin-license-${license.id || index}`}>
                                        <span>{license.name}</span>
                                        <span className="note">{license.bundleName}</span>
                                    </li>
                                ))}
                            </ul>
                            }
                        </div>
                        }
                        {(marketplaceEnabled || purchasedAssets.length > 0) &&
                            <div className="account-asset-entitlements row-section">
                                <div className="title m-b-8">Asset entitlements</div>
                                {marketplaceEnabled && purchasedAssets.length === 0 &&
                                    <>
                                        <div className="note">No asset entitlements were found.</div>
                                        <a
                                            className="btn account-asset-entitlements__explore"
                                            href={getProductioncrateSiteHref({environment: getEnvironment(), pathname: '/search/new?main_category=all'})}
                                        >
                                            Explore assets
                                        </a>
                                    </>
                                }
                                {purchasedAssets.length > 0 &&
                                <>
                                    <div className="claim">Assets available to your account.</div>
                                    <ul className="license-list">
                                        {purchasedAssets.map((purchase, index) => (
                                            <li className="item" key={`account-asset-entitlement-${purchase.id || index}`}>
                                                <a href={getProductioncrateSiteHref({environment: getEnvironment(), pathname: '/my-assets'})}>{purchase.asset_name || purchase.name || 'Asset entitlement'}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                                }
                            </div>
                        }
                    </>
                    }
                    {selectedTab==='team' && 
                    <>
                        <div className="team row-section">
                            <div className="title m-b-8">
                                Team members ({teamMembersUsed} / {teamMembersTotal})
                            </div>
                            <ul className="member-list ">
                            {teamMembers.length > 0 && teamMembers.map((item, index) => 
                                <li className="item m-b-8" key={`member-list-${index}`}>
                                    <div className="m-r-4" >{item.username} ({item.email})</div>
                                    {canRemoveTeamMember(item, user) && !item.isDeleting && 
                                    <span className="btn btn-secondary glyphicon glyphicon-cross" onClick={() => handleMemberDelete(index, item.id)}></span>
                                    }
                                    {canRemoveTeamMember(item, user) && item.isDeleting && 
                                    <span className="btn btn-secondary" >Removing...</span>
                                    }
                                </li>
                            )}
                            {teamMembers.length === 0 && 
                                <li className="item">{teamMembersLoading?'Loading...':'No members in your team.'}</li>
                            }
                            </ul>
                            {canUseTeamInvitations &&
                            <>
                            <div className="title m-b-8">
                                Pending Invitations
                            </div>
                            <ul className="member-list ">
                            {teamInvites.length > 0 && teamInvites.map((item, index) =>
                                <li className="item m-b-8" key={`member-list-${index}`}>
                                    <div className="m-r-4" >{item.email}</div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        disabled={!!resendingInviteIds[item.id]}
                                        onClick={() => handleInviteResend(item.id)}
                                    >
                                        {resendingInviteIds[item.id] ? 'Sending...' : 'Resend'}
                                    </button>
                                    <span className="btn btn-secondary glyphicon glyphicon-cross" onClick={() => handleInviteDelete(item.id)}></span>
                                </li>
                            )}
                            {teamInvites.length === 0 &&
                                <li className="item">{teamInvitesLoading?'Loading...':'No pending invitations.'}</li>
                            }
                            </ul>
                            <div style={{maxWidth: '500px'}}>
                                {teamMembers.length < teamMemberLimit &&
                                <>
                                    <div className="title m-b-8">
                                        Send invitation for your team
                                    </div>

                                    <form onSubmit={event => {event.preventDefault();handleInviteEmail();}} >
                                        <div className="additional-data-form">
                                            <input type="submit" style={{display: "none"}} />
                                            <Input
                                                label="Email Address"
                                                input={true}
                                                onChange={(value) => setTeamInvitesEmail(value)}
                                            />
                                            <button type="submit" className="submit-form" disabled={sendingInvite}>Send invitation</button>
                                            {inviteError && Array.isArray(inviteError) &&
                                                <div className="error">
                                                    {inviteError.map((error, k) => <div key={`error-key-${k}`}>{error}</div>)}
                                                </div>
                                            }
                                            {inviteError && !Array.isArray(inviteError) &&
                                                <div className="error">
                                                    {inviteError}
                                                </div>
                                            }
                                            {inviteSuccess && !Array.isArray(inviteSuccess) &&
                                                <div className="success">
                                                    {inviteSuccess}
                                                </div>
                                            }
                                            {sendingInvite &&
                                                <div className="normal">
                                                    Sending invitation ...
                                                </div>
                                            }
                                        </div>
                                    </form>
                                </>
                                }
                                {teamMembers.length >= teamMemberLimit &&
                                <div className="title m-b-24">
                                    :( ... No free member slots in your team.
                                </div>
                                }
                                {!!upgradeEnterprisePlans.length && user.team.subscription.plan &&
                                    <div className="m-t-24" style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
                                        {upgradeEnterprisePlans.map((plan, index) =>
                                            <div className="btn" key={`upgrade-plans-${index}`} onClick={() => upgradeEnterprise(plan)}>Get up to {plan.name.replace(/\D/g, '')} PRO members</div>
                                        )}
                                    </div>
                                }
                            </div>
                            </>
                            }
                            {showSeatsSection &&
                            <div className="seats-usage row-section">
                                <div className="title m-b-8">
                                    {teamSeatsSectionTitle}
                                </div>

                                {teamSeatsToast &&
                                <div className="seats-toast">
                                    {teamSeatsToast}
                                </div>
                                }

                                {teamSeatsLoading && !teamSeatsData &&
                                <div className="seats-skeleton">
                                    <div className="line line-lg"></div>
                                    <div className="line"></div>
                                    <div className="line"></div>
                                </div>
                                }

                                {teamSeatsError &&
                                <div className="seats-state error">
                                    <span>{teamSeatsError}</span>
                                    <div className="btn" onClick={handleRetryTeamSeats}>
                                        Retry
                                    </div>
                                </div>
                                }

                                {teamSeatsData &&
                                <div className="seats-context-grid">
                                    {teamSeatContextsData.map((seatContext) => (
                                        <div
                                            className={`seats-context-card ${seatContext.isDisabled ? 'disabled' : ''}`}
                                            key={`seats-context-${seatContext.key}`}
                                        >
                                            <div className="seats-context-head">
                                                <div className="subtitle">{seatContext.label}</div>
                                                {seatContext.isDisabled &&
                                                <div className="chip seats-disabled-badge">Disabled</div>
                                                }
                                            </div>

                                            <div className="seats-kpis">
                                                <div className="kpi">
                                                    <span className="label">Total</span>
                                                    <span className="value">{seatContext.seatsTotal}</span>
                                                </div>
                                                <div className="kpi">
                                                    <span className="label">Used</span>
                                                    <span className="value">{seatContext.seatsUsed}</span>
                                                </div>
                                                <div className="kpi">
                                                    <span className="label">Available</span>
                                                    <span className="value">{seatContext.seatsAvailable}</span>
                                                </div>
                                            </div>

                                            <div className="seats-bar-wrapper">
                                                <div className="seats-bar-track">
                                                    <div className="seats-bar-fill" style={{width: `${seatContext.seatsUsagePercent}%`}}></div>
                                                </div>
                                                <div className="note">
                                                    {`${seatContext.seatsUsed} / ${seatContext.seatsTotal} seats in use`}
                                                </div>
                                            </div>

                                            {seatContext.isDisabled &&
                                            <div className="note">Disabled</div>
                                            }

                                            {!seatContext.isDisabled &&
                                            <>
                                                <div className="seats-by-user m-t-12">
                                                    <div className="subtitle">Seats by user</div>
                                                    {Array.isArray(seatContext.payload.by_user) && seatContext.payload.by_user.length > 0 &&
                                                    <ul className="seats-list">
                                                        {seatContext.payload.by_user.map((item, index) => (
                                                            <li className="item" key={`seats-by-user-${seatContext.key}-${item.user_id || index}`}>
                                                                <div className="identity">
                                                                    {getUserDisplayName(item.first_name, item.last_name, item.email, item.user_id)}
                                                                </div>
                                                                <div className="meta">{item.email || 'No email'}</div>
                                                                <div className="chip">{`${item.seats_used} active`}</div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    }
                                                    {(!Array.isArray(seatContext.payload.by_user) || seatContext.payload.by_user.length === 0) &&
                                                    <div className="note">No active user seat usage right now.</div>
                                                    }
                                                </div>

                                                <div className="seats-active-sessions m-t-12">
                                                    <div className="subtitle">Active sessions</div>
                                                    {!canRevokeSeats &&
                                                    <div className="note m-t-8">Seat revocation is not available for your current permissions.</div>
                                                    }
                                                    {Array.isArray(seatContext.payload.seats) && seatContext.payload.seats.length > 0 &&
                                                    <ul className="seats-list">
                                                        {seatContext.payload.seats.map((seat, index) => {
                                                            const remainingMs = getSeatRemainingMs(seat?.expires_at);
                                                            const countdownPercent = getSeatCountdownPercent(seat);
                                                            const isSeatExpired = remainingMs <= 0;
                                                            const seatStateKey = getSeatStateKey(seat);
                                                            const seatDeviceLabel = getSeatDeviceLabel(seat, seatContext.key);
                                                            const isRevokingSeat = seatStateKey ? !!revokingSeatIds[seatStateKey] : false;
                                                            const isCurrentSeat = getIsCurrentSeat(seat);
                                                            const disableRevokeButton = !canRevokeSeats || isRevokingSeat || isCurrentSeat || !seatStateKey;

                                                            return (
                                                            <li className="item session-item" key={`seat-active-${seatContext.key}-${seat.seat_id || index}`}>
                                                                <div className="identity">
                                                                    {getUserDisplayName(seat?.user?.first_name, seat?.user?.last_name, seat?.user?.email, seat?.user?.id)}
                                                                </div>
                                                                <div className="meta">
                                                                    {`Device: ${seatDeviceLabel}`}
                                                                </div>
                                                                <div className="meta">
                                                                    {`Last used: ${formatSeatDateTime(seat?.last_used_at)}`}
                                                                </div>
                                                                <div className="seat-live-timer">
                                                                    <div className="seat-live-header">
                                                                        <div className={`countdown ${isSeatExpired ? 'expired' : ''}`}>
                                                                            {isSeatExpired ? 'Expired' : formatSeatCountdown(remainingMs)}
                                                                        </div>
                                                                        <div className="revoke-seat-row">
                                                                            {isCurrentSeat &&
                                                                            <div className="chip current-seat-badge">Current</div>
                                                                            }
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-danger revoke-seat-icon"
                                                                                title={isCurrentSeat ? 'Current active session' : 'Revoke seat'}
                                                                                disabled={disableRevokeButton}
                                                                                onClick={() => handleRevokeSeat(seat)}
                                                                            >
                                                                                {!isRevokingSeat &&
                                                                                <span className="glyphicon glyphicon-cross"></span>
                                                                                }
                                                                                {isRevokingSeat &&
                                                                                <span className="loading-indicator">...</span>
                                                                                }
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <div className="session-progress-track">
                                                                        <div className={`session-progress-fill ${isSeatExpired ? 'expired' : ''}`} style={{width: `${countdownPercent}%`}}></div>
                                                                    </div>
                                                                    <div className="meta">
                                                                        {isSeatExpired ? 'Seat expired, pending release' : 'Live countdown to expiration'}
                                                                    </div>
                                                                </div>
                                                            </li>
                                                            );
                                                        })}
                                                    </ul>
                                                    }
                                                    {(!Array.isArray(seatContext.payload.seats) || seatContext.payload.seats.length === 0) &&
                                                    <div className="note">No active sessions.</div>
                                                    }
                                                </div>
                                            </>
                                            }
                                        </div>
                                    ))}
                                </div>
                                }
                            </div>
                            }
                        </div>
                    </>
                    }
                </div>
            </div>
            }
            {false && <Footer />}
            <SubscriptionUpgradeModals ref={subscriptionUpgradeModalsRef} />
        </div>
    );
};

export default Account;
